import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl, baseurImage } from "../../Baseurl";
import EventBus from "../../Context/EventBus";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useNavigate, NavLink } from "react-router-dom";
import logo from "../../assests/logo.png";
import axiosInstance from "../../api/axiosInstance";

// Services and pharmacy rows are stored with `amount`/`price` already multiplied by
// quantity, so derive the single (unit) price for any "Price" column.
const toUnitPrice = (total, quantity) => {
  const qty = Number(quantity) > 0 ? Number(quantity) : 1;
  return Number(total || 0) / qty;
};

export default function PatientBilling({ patientId }) {
  const user = useSelector((state) => state.user.user);
  const refreshKey = useSelector((state) => state.lab.refreshKey);
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings);
  const activeBranchId = useSelector((state) => state.business.activeBranchId);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [services, setServices] = useState([]);
  const [showDateRangeDialog, setShowDateRangeDialog] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setFullYear(new Date().getFullYear()))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [inventory, setInventory] = useState([]);
  const [labRequests, setLabRequests] = useState([]);
  const [selected, setSelected] = useState({
    services: {},
    inventory: {},
    labs: {},
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [payDetailOpen, setPayDetailOpen] = useState(false);
  const [activePayment, setActivePayment] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkPayment, setLinkPayment] = useState(null);
  const [linkRows, setLinkRows] = useState([{ invoice_id: "", amount: "" }]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [InsAmount, setInsAmount] = useState(0);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [paymentSearchTerm, setPaymentSearchTerm] = useState("");

  // Installment plans state
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [installmentPlansLoading, setInstallmentPlansLoading] = useState(false);
  const [createInstallmentOpen, setCreateInstallmentOpen] = useState(false);
  const [createInstallmentLoading, setCreateInstallmentLoading] = useState(false);
  const [createInstallmentForm, setCreateInstallmentForm] = useState({
    invoice_ids: [],
    plan_type: "months",
    months: "",
    installment_amount: "",
    start_date: new Date().toISOString().split("T")[0],
    frequency: "monthly",
    notes: "",
  });
  const [viewPlanOpen, setViewPlanOpen] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [activePlanLoading, setActivePlanLoading] = useState(false);
  const [payInstallmentOpen, setPayInstallmentOpen] = useState(false);
  const [payInstallmentLoading, setPayInstallmentLoading] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [payInstallmentForm, setPayInstallmentForm] = useState({
    amount: "",
    payment_method: "",
    transaction_id: "",
  });

  // Utility function to truncate text with ellipsis
  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    const str = String(text);
    return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
  };

  const hospitalSettings =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("businessSetting"))
      : null;

  // const hospitalName = hospitalSettings?.official_name || "Hospital Name";
  const hospitalCR = hospitalSettings?.cr_number || "";
  const hospitalVAT = hospitalSettings?.vat_number || "";
  const hospitalEmail = hospitalSettings?.email || "abcd@gmail.com";
  const hospitalPhone = hospitalSettings?.telephone_1 || "+973 35135498";

  const businessSetting = JSON.parse(localStorage.getItem("businessSetting"));

  const phone = businessSetting?.telephone_1 || "N/A";
  const hospitalName = businessSetting?.official_name || "N/A";
  const email = businessSetting?.email || "N/A";
  const website = businessSetting?.website;

  const [hospitalLogo, setHospitalLogo] = useState(logo);
  const [recordLockHours, setRecordLockHours] = useState(null);

  useEffect(() => {
    axiosInstance.get('getBusinessSettings')
      .then(res => {
        const h = res.data?.data?.record_lock_hours
        if (h != null) setRecordLockHours(Number(h))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const loadLogo = async () => {
      try {
        // 1. Try to load from cache first
        const cached = localStorage.getItem("businessSettingImage");
        if (cached && cached.startsWith("data:image/")) {
          setHospitalLogo(cached);
        }

        // 2. Load from business settings
        const settings = JSON.parse(localStorage.getItem("businessSetting"));
        const bgImage = settings?.base64Image; // naming from PatientForms
        const img = settings?.image; // naming from PatientBilling

        // PatientForms prioritizes 'base64Image' which could be a filename OR base64
        const source = bgImage || img;

        if (source) {
          if (source.startsWith("data:image/")) {
            setHospitalLogo(source);
            localStorage.setItem("businessSettingImage", source);
          } else if (!source.startsWith("data:text/html")) {
            // It is a URL or filename
            let url = source;
            if (!source.startsWith("http")) {
              if (bgImage === source) {
                // PatientForms logic: assumes it's in business_setting folder
                url = `${baseurImage}business_setting/${source}`;
              } else {
                // PatientBilling logic: assumes baseurImage handles path
                url = `${baseurImage}${source}`;
              }
            }

            const response = await fetch(url);
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.startsWith("image/")) {
              const blob = await response.blob();
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64 = reader.result;
                setHospitalLogo(base64);
                localStorage.setItem("businessSettingImage", base64);
              };
              reader.readAsDataURL(blob);
            }
          }
        }
      } catch (error) {
        console.error("Error loading logo:", error);
      }
    };
    loadLogo();
  }, []);

  const navigate = useNavigate();
  // Branch selectors for invoice/payment modals
  const [invoiceBranchId, setInvoiceBranchId] = useState(null);
  const [payBranchId, setPayBranchId] = useState(null);

  // Payment modal state
  const [payOpen, setPayOpen] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payForm, setPayForm] = useState({
    invoice_id: "",
    amount: "",
    payment_method: "",
    transaction_id: "",
    is_advance: false,
  });
  const [payMeta, setPayMeta] = useState({ cashier: "", time: "" });
  const [remainingInvoices, setRemainingInvoices] = useState([]);
  const [billingSummary, setBillingSummary] = useState({
    patient_name: "",
    pendingBalance: 0,
    totalPayments: 0,
    totalBilled: 0,
    insuranceBalance: 0,
    lastPaymentDate: "",
  });

  const [currentInvoicePage, setCurrentInvoicePage] = useState(1);
  const [currentPaymentPage, setCurrentPaymentPage] = useState(1);
  // const servicesPerPage = 5; // show 5 rows per page

  const [termsCondition, setTermsCondition] = useState(null);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await axiosInstance.get("getTermsCondition/1");
        if (response.data.success && response.data.data) {
          const termData = Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data;
          setTermsCondition(termData);
        }
      } catch (error) {
        console.error("Error fetching terms:", error);
      }
    };
    fetchTerms();
  }, []);

  // const inventoryPerPage = 5; // show 5 rows per page
  const invoicesPerPage = 10;
  const paymentsPerPage = 10;

  useEffect(() => {
    if (!patientId) return;
    loadInvoices();
    loadBillables();
    loadLabRequests();
    loadPayments();
    loadRemainingInvoices();
    loadBillingSummary();
    loadPaymentMethods();
    InsuranceBalance();
    loadPaymentMethods();
    InsuranceBalance();
    loadInstallmentPlans();
  }, [patientId, refreshKey]);

  useEffect(() => {
    if (isOpen) {
      loadBillables();
      loadLabRequests();
      setInvoiceBranchId(activeBranchId || parseInt(localStorage.getItem('activeBranchId')) || (allBranchSettings.length === 1 ? allBranchSettings[0]?.id : null));
    }
  }, [isOpen]);

  // Refresh remaining invoices when Payment modal opens
  useEffect(() => {
    if (payOpen) {
      loadRemainingInvoices();
      setPayBranchId(activeBranchId || parseInt(localStorage.getItem('activeBranchId')) || (allBranchSettings.length === 1 ? allBranchSettings[0]?.id : null));
      const getLoggedInUserName = () => {
        return (
          user?.fullName ||
          user?.shortName ||
          user?.name ||
          user?.username ||
          "User"
        );
      };
      const nowStr = new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      setPayMeta({ cashier: getLoggedInUserName(), time: nowStr });
    }
  }, [payOpen]);

  // Listen to global billing changes (services/medicines added/updated/deleted)
  useEffect(() => {
    const off = EventBus.on("billing:dataChanged", async () => {
      await Promise.all([
        loadBillables(),
        loadLabRequests(),
        loadInvoices(),
        loadRemainingInvoices(),
        loadBillingSummary(),
        loadInstallmentPlans(),
      ]);
    });
    return () => off();
  }, []);

  const loadLabRequests = async () => {
    try {
      const res = await axiosInstance.get(
        `getLabRequestsUnbilledByPatientId/${patientId}`
      );
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];

      // Filter out billed items
      const eligibleLabs = list.filter((x) => {
        const billedStatus = x.billing_status;
        const billedFlag =
          x.billed ??
          x.isBilled ??
          x.is_billed ??
          (billedStatus === "Billed" ? 1 : 0);
        return String(billedFlag) !== "1";
      });

      setLabRequests(eligibleLabs);
    } catch (_) {
      setLabRequests([]);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    //step 1
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This payment will be reversed and invoice amounts will be updated!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axiosInstance.delete(`deletePaymentInvoice/${paymentId}`);
      Swal.fire(
        "Reversed!",
        "Payment has been revrersed and Deleted",
        "success"
      );

      await Promise.all([
        loadPaymentMethods(),
        loadPayments(),
        loadInvoices(),
        loadRemainingInvoices(),
        loadBillingSummary(),
        loadBillables(),
        loadLabRequests(),
      ]);
    } catch (error) {
      console.error("Delete payment failed:", error);

      const msg = error.response?.data?.message || "Failed to reverse payment";

      Swal.fire("Error", msg, "error");
    }
  };



  const loadInvoices = async () => {
    try {
      const res = await axiosInstance.get(
        `getAllInvoicesByPatient/${patientId}`
      );
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setInvoices(list);
    } catch (_) {
      setInvoices([]);
    }
  };

  const loadRemainingInvoices = async () => {
    try {
      const res = await axiosInstance.get(`getRemainingInvoices/${patientId}`);
      const list = Array.isArray(res.data?.invoices)
        ? res.data.invoices
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
      setRemainingInvoices(list);
    } catch (_) {
      setRemainingInvoices([]);
    }
  };

  const loadInstallmentPlans = async () => {
    try {
      setInstallmentPlansLoading(true);
      const res = await axiosInstance.get(`getInstallmentPlansByPatient/${patientId}`);
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setInstallmentPlans(list);
    } catch (_) {
      setInstallmentPlans([]);
    } finally {
      setInstallmentPlansLoading(false);
    }
  };

  const openCreateInstallment = () => {
    setCreateInstallmentForm({
      invoice_ids: [],
      plan_type: "months",
      months: "",
      installment_amount: "",
      start_date: new Date().toISOString().split("T")[0],
      frequency: "monthly",
      notes: "",
    });
    loadRemainingInvoices();
    setCreateInstallmentOpen(true);
  };

  const toggleInstallmentInvoice = (invoiceId) => {
    setCreateInstallmentForm((prev) => {
      const idStr = String(invoiceId);
      const exists = prev.invoice_ids.includes(idStr);
      return {
        ...prev,
        invoice_ids: exists
          ? prev.invoice_ids.filter((id) => id !== idStr)
          : [...prev.invoice_ids, idStr],
      };
    });
  };

  const buildInstallmentPreview = (form, invoicesList) => {
    const selected = invoicesList.filter((inv) =>
      form.invoice_ids.includes(
        String(inv.invoiceId || inv.invoice_id || inv.id || inv.invoiceid)
      )
    );
    const totalAmount = selected.reduce(
      (sum, inv) =>
        sum + Number(inv.remaining_amount ?? inv.remaining ?? inv.remainingAmount ?? 0),
      0
    );

    if (totalAmount <= 0) return { totalAmount: 0, schedule: [] };

    let numberOfInstallments;
    let regularAmount;
    if (form.plan_type === "months") {
      const months = Number(form.months);
      if (!months || months <= 0) return { totalAmount, schedule: [] };
      numberOfInstallments = Math.round(months);
      regularAmount = Number((totalAmount / numberOfInstallments).toFixed(3));
    } else {
      const amt = Number(form.installment_amount);
      if (!amt || amt <= 0) return { totalAmount, schedule: [] };
      regularAmount = Number(amt.toFixed(3));
      numberOfInstallments = Math.ceil(totalAmount / regularAmount);
    }

    const schedule = [];
    let allocated = 0;
    for (let i = 0; i < numberOfInstallments; i++) {
      const isLast = i === numberOfInstallments - 1;
      const amount = isLast ? Number((totalAmount - allocated).toFixed(3)) : regularAmount;
      allocated += amount;
      const dueDate = new Date(form.start_date || new Date());
      if (form.frequency === "weekly") {
        dueDate.setDate(dueDate.getDate() + i * 7);
      } else {
        dueDate.setMonth(dueDate.getMonth() + i);
      }
      schedule.push({ installment_no: i + 1, due_date: dueDate, amount });
    }

    return { totalAmount, numberOfInstallments, regularAmount, schedule };
  };

  const submitCreateInstallment = async () => {
    if (createInstallmentForm.invoice_ids.length === 0) {
      Swal.fire("Missing", "Please select at least one invoice", "info");
      return;
    }
    if (createInstallmentForm.plan_type === "months" && !createInstallmentForm.months) {
      Swal.fire("Missing", "Please enter number of months", "info");
      return;
    }
    if (createInstallmentForm.plan_type === "amount" && !createInstallmentForm.installment_amount) {
      Swal.fire("Missing", "Please enter installment amount", "info");
      return;
    }
    try {
      setCreateInstallmentLoading(true);
      const payload = {
        patient_id: Number(patientId),
        invoice_ids: createInstallmentForm.invoice_ids.map(Number),
        plan_type: createInstallmentForm.plan_type,
        months: createInstallmentForm.plan_type === "months" ? Number(createInstallmentForm.months) : undefined,
        installment_amount:
          createInstallmentForm.plan_type === "amount" ? Number(createInstallmentForm.installment_amount) : undefined,
        start_date: createInstallmentForm.start_date,
        frequency: createInstallmentForm.frequency,
        notes: createInstallmentForm.notes || undefined,
        branch_id: activeBranchId || null,
      };
      await axiosInstance.post(`createInstallmentPlan`, payload);
      Swal.fire("Success", "Installment plan created successfully", "success");
      setCreateInstallmentOpen(false);
      await Promise.all([
        loadInstallmentPlans(),
        loadInvoices(),
        loadRemainingInvoices(),
        loadBillingSummary(),
      ]);
      EventBus.dispatch("billing:dataChanged");
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to create installment plan";
      Swal.fire("Error", msg, "error");
    } finally {
      setCreateInstallmentLoading(false);
    }
  };

  const openViewPlan = async (plan) => {
    setViewPlanOpen(true);
    setActivePlanLoading(true);
    try {
      const res = await axiosInstance.get(`getInstallmentPlanById/${plan.id}`);
      setActivePlan(res.data?.data || null);
    } catch (_) {
      setActivePlan(null);
    } finally {
      setActivePlanLoading(false);
    }
  };

  const openPayInstallment = (schedule) => {
    setActiveSchedule(schedule);
    setPayInstallmentForm({
      amount: (Number(schedule.amount) - Number(schedule.paid_amount)).toFixed(3),
      payment_method: paymentMethods.length > 0 ? paymentMethods[0].name : "Cash",
      transaction_id: "",
    });
    setPayInstallmentOpen(true);
  };

  const submitPayInstallment = async () => {
    if (!payInstallmentForm.amount || !payInstallmentForm.payment_method) {
      Swal.fire("Missing", "Amount and payment method are required", "info");
      return;
    }
    try {
      setPayInstallmentLoading(true);
      await axiosInstance.post(`payInstallment`, {
        schedule_id: activeSchedule.id,
        amount: Number(payInstallmentForm.amount),
        payment_method: payInstallmentForm.payment_method,
        transactionId: payInstallmentForm.transaction_id || undefined,
        branch_id: activeBranchId || null,
      });
      Swal.fire("Success", "Installment payment recorded successfully", "success");
      setPayInstallmentOpen(false);
      await Promise.all([
        loadInstallmentPlans(),
        loadInvoices(),
        loadPayments(),
        loadRemainingInvoices(),
        loadBillingSummary(),
      ]);
      if (activePlan) {
        const res = await axiosInstance.get(`getInstallmentPlanById/${activePlan.id}`);
        setActivePlan(res.data?.data || null);
      }
      EventBus.dispatch("billing:dataChanged");
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to record installment payment";
      Swal.fire("Error", msg, "error");
    } finally {
      setPayInstallmentLoading(false);
    }
  };

  const handleCancelInstallmentPlan = async (plan) => {
    const confirm = await Swal.fire({
      title: "Cancel this installment plan?",
      text: "Unpaid installments will no longer be tracked.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Cancel Plan",
      cancelButtonText: "Keep Plan",
    });
    if (!confirm.isConfirmed) return;
    try {
      await axiosInstance.put(`cancelInstallmentPlan/${plan.id}`);
      Swal.fire("Cancelled", "Installment plan has been cancelled", "success");
      await loadInstallmentPlans();
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to cancel installment plan";
      Swal.fire("Error", msg, "error");
    }
  };

  const printInstallmentStatement = async (plan) => {
    let d = plan;
    try {
      const res = await axiosInstance.get(`getInstallmentPlanById/${plan.id}`);
      d = res.data?.data || plan;
    } catch (_) {
      // fall back to the summary row already in hand
    }

    let patientDetails = {};
    try {
      const res = await axiosInstance.get(`getPatientById/${patientId}`);
      patientDetails = res.data?.data || {};
    } catch (_) {
      // proceed without extra patient details
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const totalAmount = Number(d.total_amount || 0);
    const totalPaid = (d.schedule || []).reduce((sum, s) => sum + Number(s.paid_amount || 0), 0);
    const remaining = Math.max(totalAmount - totalPaid, 0);

    const invoiceRows = (d.invoices || [])
      .map(
        (inv, idx) => `<tr>
        <td style="border: 1px solid #000;">${idx + 1}</td>
        <td style="border: 1px solid #000;">${inv.invoice_no || inv.invoice_id}</td>
        <td style="border: 1px solid #000; text-align:right">${Number(inv.total_amount || 0).toFixed(3)} BHD</td>
        <td style="border: 1px solid #000; text-align:right">${Number(inv.allocated_amount || 0).toFixed(3)} BHD</td>
      </tr>`
      )
      .join("");

    const scheduleRows = (d.schedule || [])
      .map((row) => {
        const statusLabel = row.status.charAt(0).toUpperCase() + row.status.slice(1);
        return `<tr>
        <td style="border: 1px solid #000;">${row.installment_no}</td>
        <td style="border: 1px solid #000;">${new Date(row.due_date).toLocaleDateString("en-GB")}</td>
        <td style="border: 1px solid #000; text-align:right">${Number(row.amount || 0).toFixed(3)} BHD</td>
        <td style="border: 1px solid #000; text-align:right">${Number(row.paid_amount || 0).toFixed(3)} BHD</td>
        <td style="border: 1px solid #000; text-align:right">${(Number(row.amount || 0) - Number(row.paid_amount || 0)).toFixed(3)} BHD</td>
        <td style="border: 1px solid #000;">${statusLabel}</td>
      </tr>`;
      })
      .join("");

    const paymentRows = (d.payments || [])
      .map(
        (p, idx) => `<tr>
        <td style="border: 1px solid #000;">${idx + 1}</td>
        <td style="border: 1px solid #000;">${new Date(p.payment_date).toLocaleDateString("en-GB")}</td>
        <td style="border: 1px solid #000;">${p.invoice_id || "-"}</td>
        <td style="border: 1px solid #000;">${p.payment_method || "-"}</td>
        <td style="border: 1px solid #000;">${p.transactionId || "-"}</td>
        <td style="border: 1px solid #000;">${p.sent_by_name || "-"}</td>
        <td style="border: 1px solid #000; text-align:right">${Number(p.payment_amount || 0).toFixed(3)} BHD</td>
      </tr>`
      )
      .join("");

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
<title>Installment Plan Statement #${d.id}</title>
<style>
@page {
  size: A4;
  margin: 15mm 12mm 18mm 12mm;
}
* {
  box-sizing: border-box;
}
html, body {
  width: 100%;
  margin: 0;
  padding: 0;
}
body {
font-family: Arial, sans-serif;
line-height: 1.2;
}
table { page-break-inside: auto; }
tr { page-break-inside: avoid; page-break-after: auto; }
thead { display: table-header-group; }
tfoot { display: table-footer-group; }
.logo{
text-align:center}
.top-section {
display: flex;
justify-content: space-between;
margin-top: 20px;
}
.billingInvoice h2 {
font-size: 25px;
font-weight: 600;
}
.patient-info {
text-align: left;
font-size: 14px;
}
.invoice-title {
margin-top: 30px;
text-align: center;
}
.billingInvoice p,.billingInvoice h2 {
margin-top:0px;
margin-bottom:0px}
.paymentLine {
display:flex;
justify-content:end;
margin-top:50px;
}
.paymentLine p:first-child{
margin-right:2px;
padding-right:2px;
border-right:1px solid #000;
}
table th,table td{
text-align:left;
padding:4px 10px;
}
.terms strong{
margin-left:5px}
.footer{
  margin-top: 24px;
  border-top: 1px solid #eee;
  padding-top: 10px;
  font-size: 11px;
  text-align: center;
  color: #666;
}
</style>
</head>
<body>
<div class="billingInvoice">
<div class="logo">
<img height=100 src="${hospitalLogo}" />
</div>
<div class="top-section">
<div class="hospital-info">
<h2>${hospitalName}</h2>
<p><strong>CR:</strong> ${hospitalCR}</p>
<p><strong>VAT No.</strong> ${hospitalVAT}</p>
</div>

<div class="patient-info">
<p style="margin: 0; padding: 0;"><strong>Patient Name: </strong> ${billingSummary.patient_name}</p>
<p style="margin: 0; padding: 0;"><strong>Patient ID: </strong> ${patientDetails.fileNumber || patientId}</p>
<p style="margin: 0; padding: 0;"><strong>Mobile: </strong> ${patientDetails.mobileNumber || "-"}</p>
</div>
</div>
<div class="invoice-title">
<h2>Installment Plan Statement #${d.id}</h2>
<p style="margin-top:20px"><strong>Start Date:</strong> ${new Date(d.start_date).toLocaleDateString("en-GB")}</p>
<p><strong>Frequency:</strong> ${(d.frequency || "").charAt(0).toUpperCase() + (d.frequency || "").slice(1)}</p>
<p><strong>Status:</strong> ${(d.status || "").charAt(0).toUpperCase() + (d.status || "").slice(1)}</p>
</div>
</div>

<div class="paymentLine">
<p> <strong>Total Paid: <span style="color:#318965">${totalPaid.toFixed(3)} BHD</span> </strong></p>
<p> <strong>Remaining Balance: <span style="color:#dc2626">${remaining.toFixed(3)} BHD</span> </strong></p>
</div>

<h5 style="margin-top:20px">Invoices Covered</h5>
<table style="width: 100%; border-collapse: collapse; font-size: 12px;">
<thead>
<tr>
<th style="border: 1px solid #000;">#</th>
<th style="border: 1px solid #000;">Invoice</th>
<th style="border: 1px solid #000;">Invoice Total</th>
<th style="border: 1px solid #000;">Allocated to Plan</th>
</tr>
</thead>
<tbody>
${invoiceRows || '<tr><td colspan="4" style="text-align:center; border:1px solid #000;">No invoices found</td></tr>'}
</tbody>
<tfoot>
<tr>
<td colspan="3" style="border: 1px solid #000; text-align: right;"><strong>Total Amount</strong></td>
<td style="border: 1px solid #000; text-align: right;"><strong>${totalAmount.toFixed(3)} BHD</strong></td>
</tr>
</tfoot>
</table>

<h5 style="margin-top:30px">Installment Schedule</h5>
<table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-top:10px">
<thead>
<tr>
<th style="border: 1px solid #000;">#</th>
<th style="border: 1px solid #000;">Due Date</th>
<th style="border: 1px solid #000;">Amount</th>
<th style="border: 1px solid #000;">Paid</th>
<th style="border: 1px solid #000;">Balance</th>
<th style="border: 1px solid #000;">Status</th>
</tr>
</thead>
<tbody>
${scheduleRows || '<tr><td colspan="6" style="text-align:center; border:1px solid #000;">No schedule found</td></tr>'}
</tbody>
</table>

<h5 style="margin-top:30px">Payment History</h5>
<table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-top:10px">
<thead>
<tr>
<th style="border: 1px solid #000;">#</th>
<th style="border: 1px solid #000;">Date</th>
<th style="border: 1px solid #000;">Invoice</th>
<th style="border: 1px solid #000;">Method</th>
<th style="border: 1px solid #000;">TRN-ID</th>
<th style="border: 1px solid #000;">Added By</th>
<th style="border: 1px solid #000;">Amount</th>
</tr>
</thead>
<tbody>
${paymentRows || '<tr><td colspan="7" style="text-align:center; border:1px solid #000;">No payments recorded</td></tr>'}
</tbody>
<tfoot>
<tr>
<td colspan="6" style="border: 1px solid #000; text-align: right;"><strong>Total Paid</strong></td>
<td style="border: 1px solid #000; text-align: right;"><strong>${totalPaid.toFixed(3)} BHD</strong></td>
</tr>
</tfoot>
</table>

${d.notes ? `<h5 style="margin-top:30px">Notes</h5>
<div style="margin-top:10px;padding:10px; border: 1px solid #eee; border-radius: 4px; font-size: 11px; white-space: pre-wrap;">${d.notes}</div>` : ""}

<h5 style="margin-top:50px">Terms & Conditions</h5>
<div style="margin-top:10px;padding:10px; border: 1px solid #eee; border-radius: 4px; font-size: 11px; white-space: pre-wrap;">
  ${termsCondition?.content || "Thank you for your business."}
</div>

</div>

<div class="footer">
        Hospital Name: ${hospitalName} | Email: ${email} | Phone: ${phone} | Website: ${website}
</div>

<script>
  window.onload = () => {
      setTimeout(() => {
          window.print();
      }, 500);

      window.onafterprint = () => {
          window.close();
      };
  };
</script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Load billing summary data
  const loadBillingSummary = async () => {
    try {
      const res = await axiosInstance.get(
        `getPatientPaymentSummary/${patientId}`
      );
      const data = res.data?.data || res.data || {};

      const totalPayments = Number(data.total_payments || 0);
      const totalBilled   = Number(data.total_billed   || 0);
      setBillingSummary({
        patient_name: data.patient_name || "",
        pendingBalance: Number(data.pending_balance || 0),
        totalPayments,
        totalBilled,
        insuranceBalance: Number(data.insurance_balance || 0),
        lastPaymentDate: data.last_payment_date || "",
      });
    } catch (error) {
      console.error("Error loading billing summary:", error);
      // Keep default values if API fails
    }
  };

  const InsuranceBalance = async () => {
    try {
      const ins = await axiosInstance.get(
        `getInsuranceClaimsByPatientId/${patientId}`
      );
      const amount = Number(
        ins.data?.totalInsuranceAmount ??
        ins.data?.data?.totalInsuranceAmount ??
        0
      );
      setInsAmount(isNaN(amount) ? 0 : amount);
    } catch (error) {
      console.error("Error loading insurance balance:", error);
      setInsAmount(0);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await axiosInstance.get(`getAllPaymentMethods`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setPaymentMethods(list);

      if (list.length > 0) {
        setPayForm((prev) => ({ ...prev, payment_method: list[0].name }));
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
      // Fallback to default methods
      setPaymentMethods([
        { id: 1, name: "Cash" },
        { id: 2, name: "Card" },
        { id: 3, name: "Transfer" },
        { id: 4, name: "Cheque" },
      ]);
      setPayForm((prev) => ({ ...prev, payment_method: "Cash" }));
    }
  };

  const loadPayments = async () => {
    try {
      const res = await axiosInstance.get(`getPaymentsByPatient/${patientId}`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setPayments(list);
    } catch (_) {
      setPayments([]);
    }
  };

  const openPaymentForInvoice = (invoice) => {
    const numericId =
      invoice?.invoiceId ||
      invoice?.invoice_id ||
      (() => {
        const invNoCandidate =
          invoice?.invoice_no || invoice?.invoiceNo || invoice?.id || "";
        const match = remainingInvoices.find(
          (x) => (x.invoice_no || x.invoiceNo) === invNoCandidate
        );
        return match?.invoiceId || match?.invoice_id || match?.id;
      })();
    const remaining =
      invoice?.remaining_amount ?? invoice?.remaining ?? invoice?.amount ?? "";
    setActiveInvoice(invoice || null);
    setPayForm((prev) => ({
      ...prev,
      invoice_id: numericId ? String(numericId) : "",
      amount: String(remaining),
    }));
    loadRemainingInvoices();
    setPayOpen(true);
  };

  const openPaymentBlank = () => {
    setActiveInvoice(null);
    setPayForm({
      invoice_id: "",
      amount: "",
      payment_method:
        paymentMethods.length > 0 ? paymentMethods[0].name : "Cash",
      transaction_id: "",
    });
    loadRemainingInvoices();
    setPayOpen(true);
  };

  const submitPayment = async () => {
    if (!payForm.invoice_id || !payForm.amount) {
      Swal.fire("Missing", "Invoice and amount are required", "info");
      return;
    }
    try {
      setPayLoading(true);
      const payload = {
        invoice_id: Number(payForm.invoice_id),
        amount: Number(payForm.amount),
        payment_method: payForm.payment_method,
        transactionId: payForm.transaction_id || undefined,
        branch_id: payBranchId || activeBranchId || null,
      };
      await axiosInstance.post(`addPaymentInvoice`, payload);
      Swal.fire("Success", "Payment recorded successfully", "success");
      setPayOpen(false);
      setPayForm({
        invoice_id: "",
        amount: "",
        payment_method:
          paymentMethods.length > 0 ? paymentMethods[0].name : "Cash",
        transaction_id: "",
      });
      await Promise.all([
        loadInvoices(),
        loadPayments(),
        loadRemainingInvoices(),
        loadBillingSummary(),
      ]);
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to add payment";
      Swal.fire("Error", msg, "error");
    } finally {
      setPayLoading(false);
    }
  };

  const submitAdvancePayment = async () => {
    if (!payForm.amount || !payForm.payment_method) {
      Swal.fire("Missing", "Amount and payment method are required", "info");
      return;
    }
    try {
      setPayLoading(true);
      const payload = {
        patient_id: Number(patientId),
        amount: Number(payForm.amount),
        payment_method: payForm.payment_method,
        transactionId: payForm.transaction_id || undefined,
        branch_id: payBranchId || activeBranchId || null,
      };
      await axiosInstance.post(`addAdvancePayment`, payload);
      Swal.fire("Success", "Advance payment recorded successfully", "success");
      setPayOpen(false);
      setPayForm({
        invoice_id: "",
        amount: "",
        payment_method: paymentMethods.length > 0 ? paymentMethods[0].name : "Cash",
        transaction_id: "",
        is_advance: false,
      });
      await Promise.all([loadPayments(), loadBillingSummary()]);
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to record advance payment";
      Swal.fire("Error", msg, "error");
    } finally {
      setPayLoading(false);
    }
  };

  const submitLinkAdvancePayment = async () => {
    const validRows = linkRows.filter(r => r.invoice_id && Number(r.amount) > 0);
    if (validRows.length === 0) {
      Swal.fire("Missing", "Please add at least one invoice with an amount", "info");
      return;
    }
    const totalAllocated = validRows.reduce((s, r) => s + Number(r.amount), 0);
    const paymentAmount = Number(linkPayment?.payment_amount ?? linkPayment?.amount ?? 0);
    if (totalAllocated > paymentAmount + 0.001) {
      Swal.fire("Error", `Total allocated (${totalAllocated.toFixed(3)}) exceeds payment amount (${paymentAmount.toFixed(3)})`, "error");
      return;
    }
    try {
      setLinkLoading(true);
      for (const row of validRows) {
        await axiosInstance.post(`linkAdvancePayment`, {
          payment_id: linkPayment.payment_id || linkPayment.id,
          invoice_id: Number(row.invoice_id),
          amount: Number(row.amount),
        });
      }
      Swal.fire("Success", "Advance payment linked to invoice(s)", "success");
      setLinkOpen(false);
      setLinkPayment(null);
      setLinkRows([{ invoice_id: "", amount: "" }]);
      await Promise.all([loadInvoices(), loadPayments(), loadRemainingInvoices(), loadBillingSummary()]);
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to link payment";
      Swal.fire("Error", msg, "error");
    } finally {
      setLinkLoading(false);
    }
  };

  const handleInvoiceSelect = (e) => {
    const idStr = e.target.value;
    const idNum = Number(idStr);
    const inv = remainingInvoices.find(
      (x) =>
        Number(x.invoiceId || x.invoice_id || x.id || x.invoiceid) === idNum ||
        (x.invoice_no || x.invoiceNo) === idStr
    );
    const remaining =
      inv?.remaining_amount ?? inv?.remaining ?? inv?.remainingAmount ?? "";
    setPayForm((prev) => ({
      ...prev,
      invoice_id: idStr,
      amount: remaining !== "" ? String(remaining) : prev.amount,
    }));
  };

  const fetchInvoiceDetails = async (invoice) => {
    if (!invoice) return null;
    try {
      const id = invoice.id || invoice.invoice_id || invoice.invoiceNo;
      let details = null;

      // Use the new API endpoint
      try {
        const res = await axiosInstance.get(`getInvoiceById/${id}`);
        if (res.data && res.data.success && res.data.data) {
          const apiData = res.data.data;

          const mappedServices = [
            ...(apiData.services || []).map((s) => ({
              id: s.id,
              date: s.created_at || s.updated_at || apiData.invoice_date || apiData.updated_at,
              code: s.code || s.serviceCode || s.service_code || "-",
              name: s.name || s.serviceName || s.service_name || "-",
              price: toUnitPrice(s.price ?? s.amount ?? 0, s.quantity),
              discount: s.discount ?? s.discountAmount ?? 0,
              quantity: s.quantity ?? 1,
              netAmount:
                s.netAmount ??
                s.net_amount ??
                Number(s.price ?? 0) - Number(s.discount ?? 0),
            })),
            ...(apiData.labs || []).map((l) => ({
              id: l.id,
              date: l.created_at || l.updated_at || apiData.invoice_date || apiData.updated_at,
              code: l.code || l.lab_code || l.service_code || "-",
              name: l.name || l.lab_name || l.service_name || "-",
              price: toUnitPrice(l.price ?? l.amount ?? 0, l.quantity),
              discount: l.discount ?? 0,
              quantity: l.quantity ?? 1,
              netAmount:
                l.netAmount ??
                l.net_amount ??
                Number(l.price ?? 0) - Number(l.discount ?? 0),
            })),
          ];

          const mappedInventory = [
            ...(apiData.pharmacy || apiData.inventory || []).map((i) => ({
              id: i.id || i.itemId || i.pharmacyId,
              date: i.created_at || i.updated_at || apiData.invoice_date || apiData.updated_at,
              code: i.drug_code || i.code || i.drugId || i.id || "-",
              name: i.drug_name || i.name || `Medicine #${i.drugId || i.id}`,
              price: toUnitPrice(i.price ?? i.unitPrice ?? 0, i.quantity),
              quantity: i.quantity ?? 1,
              discount: i.discount ?? 0,
              totalPrice:
                i.totalPrice ??
                i.total_price ??
                Number(i.price ?? i.unitPrice ?? 0) - Number(i.discount ?? 0),
            })),
          ];

          details = {
            invoiceNo: apiData.invoice_no || apiData.id,
            date: apiData.invoice_date || apiData.updated_at,
            services: mappedServices,
            inventory: mappedInventory,
            total: apiData.total_amount || 0,
            patientId: apiData.patient_id,
            patientName:
              apiData.patient_name ||
              apiData.fullName ||
              (apiData.firstName ? `${apiData.firstName} ${apiData.lastName}` : ""),
            paidAmount: apiData.paid_amount || 0,
            remainingAmount: apiData.remaining_amount || 0,
            status: apiData.invoice_status || apiData.status,
          };
        }
      } catch (error) {
        // Fallback to using invoice data directly if API fails
        const servicesArr = [...(invoice.services || []), ...(invoice.labs || [])];
        const pharmacyArr = invoice.pharmacy || invoice.items || invoice.inventory || [];

        details = {
          invoiceNo: invoice.invoiceNo || invoice.invoice_id || invoice.id,
          date:
            invoice.date ||
            invoice.invoice_date ||
            invoice.createdAt ||
            invoice.created_at ||
            new Date().toISOString(),
          services: servicesArr.map((s) => ({
            id: s.id,
            date: s.date || s.created_at || s.updated_at || invoice.date || invoice.invoice_date || new Date().toISOString(),
            code: s.code || s.serviceCode || s.service_code || "-",
            name: s.name || s.serviceName || s.service_name || s.lab_name || "-",
            price: toUnitPrice(s.price ?? s.amount ?? 0, s.quantity),
            discount: s.discount ?? s.discountAmount ?? 0,
            quantity: s.quantity,
            netAmount:
              s.netAmount ??
              s.net_amount ??
              Number(s.price ?? 0) - Number(s.discount ?? 0),
          })),
          inventory: pharmacyArr.map((i) => ({
            id: i.id || i.itemId || i.pharmacyId,
            date: i.date || i.created_at || i.updated_at || invoice.date || invoice.invoice_date || new Date().toISOString(),
            code: i.drug_code || i.code || i.drugId || i.id || "-",
            name: i.drug_name || i.name || `Medicine #${i.drugId || i.id}`,
            price: toUnitPrice(i.price ?? i.unitPrice ?? 0, i.quantity),
            quantity: i.quantity ?? 1,
            discount: i.discount ?? 0,
            totalPrice:
              i.totalPrice ??
              i.total_price ??
              Number(i.price ?? i.unitPrice ?? 0) - Number(i.discount ?? 0),
          })),
          total:
            invoice.total_amount ??
            invoice.total ??
            invoice.totalAmount ??
            invoice.amount ??
            0,
          patientId: invoice.patient_id || invoice.patientId || patientId,
          patientName:
            invoice.patient_name || invoice.fullName || billingSummary.patient_name,
          paidAmount: invoice.paid_amount || invoice.paidAmount || 0,
          remainingAmount: invoice.remaining_amount || invoice.remainingAmount || 0,
          status: invoice.invoice_status || invoice.status,
        };
      }

      setInvoiceDetails(details);
      return details;
    } catch (error) {
      setInvoiceDetails(null);
      return null;
    }
  };

  const openInvoiceDetail = async (invoice) => {
    setActiveInvoice(invoice);
    setDetailOpen(true);
    await fetchInvoiceDetails(invoice);
  };

  // Print a formatted tax invoice directly from the Invoices table (same layout as ViewInvoice.jsx)
  const handlePrintInvoiceRow = async (invoiceRow) => {
    const id =
      invoiceRow.id ||
      invoiceRow.invoice_id ||
      invoiceRow.invoiceId ||
      invoiceRow.invoice_no;

    let d = invoiceRow;
    try {
      const res = await axiosInstance.get(`getInvoiceById/${id}`);
      if (res.data?.success && res.data?.data) {
        d = res.data.data;
      }
    } catch (_) {
      // fall back to the row data already available in the table
    }

    const fmt = (n, dg = 3) => Number(n || 0).toFixed(dg);
    const fmtDateTime = (ts) => {
      if (!ts) return "-";
      const dt = new Date(ts);
      return `${dt.toLocaleDateString("en-GB")} ${dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
    };

    const services = d.services || [];
    const pharmacy = d.pharmacy || [];
    const labs = d.labs || [];
    const paymentsList = d.payments || [];

    const totalServiceVat = services.reduce((s, x) => s + Number(x.vat_value || 0), 0);
    const totalPharmacyVat = pharmacy.reduce((s, x) => s + Number(x.vat_value || 0), 0);
    const totalVat = totalServiceVat + totalPharmacyVat;
    const totalDiscount = services.reduce((s, x) => s + Number(x.discount_value || 0), 0);
    const grandTotal = Number(d.total_amount || 0);
    const paidAmount = Number(d.paid_amount || 0);
    const remaining = Number(d.remaining_amount ?? (grandTotal - paidAmount));

    const hs = (() => { try { return JSON.parse(localStorage.getItem("businessSetting")) || {}; } catch { return {}; } })();
    const logoRaw = localStorage.getItem("businessSettingImage") || "";
    const logoUrl = logoRaw
      ? (logoRaw.startsWith("data:") || logoRaw.startsWith("http") ? logoRaw : `${baseurImage}${logoRaw}`)
      : (hs.image ? `${baseurImage}${hs.image}` : "");

    const hospitalName = hs.official_name || hs.name || "Medical Center";
    const hospitalCR = hs.cr_number || "";
    const hospitalVAT = hs.vat_number || "";
    const hospitalTel = hs.telephone_1 || hs.phone || "";
    const hospitalEmail = hs.email || "";
    const hospitalAddr = hs.address || "";

    const serviceRows = services.map((s, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${s.serviceCode || "-"}</td>
        <td>${s.serviceName || s.name || "-"}</td>
        <td class="c">${s.quantity ?? 1}</td>
        <td class="r">${fmt(toUnitPrice(s.price, s.quantity))}</td>
        <td class="r">${fmt(s.discount_value)}</td>
        <td class="r">${s.vat_percent ?? 0}%</td>
        <td class="r">${fmt(s.vat_value)}</td>
        <td class="r"><b>${fmt(s.net_amount)}</b></td>
      </tr>`).join("");

    const pharmacyRows = pharmacy.map((p, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${p.serviceName || p.name || "-"}</td>
        <td class="c">${p.quantity ?? 1}</td>
        <td class="r">${fmt(toUnitPrice(p.price, p.quantity))}</td>
        <td class="r">${fmt(p.vat_value)}</td>
        <td class="r"><b>${fmt(p.net_amount)}</b></td>
      </tr>`).join("");

    const labRows = labs.map((l, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${l.serviceName || l.name || "-"}</td>
        <td>${l.category || "-"}</td>
        <td class="c">${l.quantity ?? 1}</td>
        <td class="r"><b>${fmt(l.net_amount)}</b></td>
      </tr>`).join("");

    const paymentRows = paymentsList.map((p, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${fmtDateTime(p.payment_date)}</td>
        <td>${p.payment_method || "-"}</td>
        <td>${p.transactionId || "-"}</td>
        <td class="r">${fmt(p.payment_amount)}</td>
      </tr>`).join("");

    const totalPayments = paymentsList.reduce((s, p) => s + Number(p.payment_amount || 0), 0);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>VAT Invoice #${d.invoice_no || d.id}</title>
  <style>
    @page { size: A4; margin: 20mm 22mm 16mm 22mm; }
    * { box-sizing: border-box; margin: 45; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; background: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* ── Clinic header ── */
    .hdr { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 8px; border-bottom: 2px solid #000; margin-bottom: 8px; }
    .hdr-logo img  { max-height: 60px; max-width: 130px; object-fit: contain; display: block; }
    .hdr-logo .hname { font-size: 16pt; font-weight: bold; }
    .hdr-right { text-align: right; font-size: 10pt; line-height: 1.55; }
    .hdr-right .clinic-name { font-size: 13pt; font-weight: bold; margin-bottom: 2px; }

    /* ── Invoice meta bar ── */
    .meta-bar { display: flex; border: 1px solid #000; margin-bottom: 8px; }
    .meta-bar .title-cell { background: #fff; color: #000; display: flex; align-items: center; justify-content: center; padding: 6px 14px; white-space: nowrap; border-right: 1px solid #000; }
    .meta-bar .title-cell span { font-size: 13pt; font-weight: bold; letter-spacing: 1px; }
    .meta-bar .cell { flex: 1; padding: 4px 8px; border-left: 1px solid #000; }
    .meta-bar .lbl { font-size: 9pt; text-transform: uppercase; color: #555; letter-spacing: 0.3px; }
    .meta-bar .val { font-size: 11pt; font-weight: bold; margin-top: 1px; }

    /* ── Bill to ── */
    .bill-to { border: 1px solid #000; padding: 5px 9px; margin-bottom: 8px; display: flex; gap: 24px; flex-wrap: wrap; }
    .bill-to .field .lbl { font-size: 9pt; text-transform: uppercase; color: #555; }
    .bill-to .field .val { font-size: 11.5pt; font-weight: bold; margin-top: 1px; }

    /* ── Section title ── */
    .sec { font-size: 10pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1.5px solid #000; padding-bottom: 2px; margin: 9px 0 3px; }

    /* ── Tables ── */
    table { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
    th { border: 1px solid #000; padding: 3px 5px; text-align: left; font-size: 10pt; background: #ebebeb; font-weight: bold; }
    td { border: 1px solid #ccc; padding: 3px 5px; vertical-align: top; }
    tbody tr:nth-child(even) td { background: #f7f7f7; }
    tfoot td { border: 1px solid #000; font-weight: bold; background: #ebebeb; }
    .r { text-align: right; }
    .c { text-align: center; }

    /* ── Totals ── */
    .totals-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
    .totals-tbl { width: 250px; border-collapse: collapse; font-size: 11pt; }
    .totals-tbl td { padding: 3px 7px; border-bottom: 1px solid #ddd; }
    .totals-tbl .grand td { border-top: 2px solid #000; border-bottom: 2px solid #000; font-weight: bold; font-size: 12pt; background: #ebebeb; }
    .totals-tbl .sub td { background: #f5f5f5; font-weight: bold; }

    /* ── Footer ── */
    .footer { margin-top: 14px; border-top: 1px solid #999; padding-top: 5px; display: flex; justify-content: space-between; font-size: 9pt; color: #444; }
  </style>
</head>
<body>

  <!-- Clinic header -->
  <div class="hdr">
    <div class="hdr-logo">
      ${logoUrl
        ? `<img src="${logoUrl}" alt="logo"/>`
        : `<div class="hname">${hospitalName}</div>`}
    </div>
    <div class="hdr-right">
      <div class="clinic-name">${hospitalName}</div>
      ${hospitalAddr ? `<div>${hospitalAddr}</div>` : ""}
      ${hospitalTel ? `<div>Tel: ${hospitalTel}</div>` : ""}
      ${hospitalEmail ? `<div>${hospitalEmail}</div>` : ""}
      ${hospitalCR ? `<div>CR: ${hospitalCR}</div>` : ""}
      ${hospitalVAT ? `<div>VAT No: ${hospitalVAT}</div>` : ""}
    </div>
  </div>

  <!-- Invoice meta bar -->
  <div class="meta-bar">
    <div class="title-cell"><span>VAT INVOICE</span></div>
    <div class="cell">
      <div class="lbl">Invoice No</div>
      <div class="val">#${d.invoice_no || d.id}</div>
    </div>
    <div class="cell">
      <div class="lbl">Date</div>
      <div class="val">${fmtDateTime(d.invoice_date || d.updated_at)}</div>
    </div>
    <div class="cell">
      <div class="lbl">Status</div>
      <div class="val">${d.invoice_status || d.status || "-"}</div>
    </div>
  </div>

  <!-- Bill To -->
  <div class="bill-to">
    <div class="field">
      <div class="lbl">Bill To</div>
      <div class="val">${d.fullName || d.patient_name || "-"}</div>
    </div>
    ${(d.mobileNumber || d.MobileNumber) ? `<div class="field"><div class="lbl">Phone</div><div class="val">${d.mobileNumber || d.MobileNumber}</div></div>` : ""}
    ${d.civil_id ? `<div class="field"><div class="lbl">Civil ID</div><div class="val">${d.civil_id}</div></div>` : ""}
  </div>

  ${services.length > 0 ? `
  <div class="sec">Services</div>
  <table>
    <thead><tr><th class="c">#</th><th>Code</th><th>Description</th><th class="c">Qty</th><th class="r">Price</th><th class="r">Discount</th><th class="r">VAT%</th><th class="r">VAT Amt</th><th class="r">Net (BHD)</th></tr></thead>
    <tbody>${serviceRows}</tbody>
  </table>` : ""}

  ${pharmacy.length > 0 ? `
  <div class="sec">Pharmacy</div>
  <table>
    <thead><tr><th class="c">#</th><th>Item</th><th class="c">Qty</th><th class="r">Unit Price</th><th class="r">VAT Amt</th><th class="r">Net (BHD)</th></tr></thead>
    <tbody>${pharmacyRows}</tbody>
  </table>` : ""}

  ${labs.length > 0 ? `
  <div class="sec">Lab Tests</div>
  <table>
    <thead><tr><th class="c">#</th><th>Test Name</th><th>Category</th><th class="c">Qty</th><th class="r">Net (BHD)</th></tr></thead>
    <tbody>${labRows}</tbody>
  </table>` : ""}

  <!-- Totals -->
  <div class="totals-wrap">
    <table class="totals-tbl">
      ${totalDiscount > 0 ? `<tr><td>Total Discount</td><td class="r">${fmt(totalDiscount)} BHD</td></tr>` : ""}
      <tr><td>Total VAT</td><td class="r">${fmt(totalVat)} BHD</td></tr>
      <tr class="grand"><td>Grand Total</td><td class="r">${fmt(grandTotal)} BHD</td></tr>
      <tr class="sub"><td>Amount Paid</td><td class="r">${fmt(paidAmount)} BHD</td></tr>
      <tr class="sub"><td>Balance Due</td><td class="r">${fmt(remaining)} BHD</td></tr>
    </table>
  </div>

  ${paymentsList.length > 0 ? `
  <div class="sec">Payment History</div>
  <table>
    <thead><tr><th class="c">#</th><th>Date &amp; Time</th><th>Method</th><th>Transaction ID</th><th class="r">Amount (BHD)</th></tr></thead>
    <tbody>${paymentRows}</tbody>
    <tfoot><tr><td colspan="4" class="r">Total Paid</td><td class="r">${fmt(totalPayments)} BHD</td></tr></tfoot>
  </table>` : ""}

  <div class="footer">
    <span>Computer-generated document — no signature required.</span>
    <span>Printed: ${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}</span>
  </div>

  <script>window.onload = () => { setTimeout(() => { window.print(); window.onafterprint = () => window.close(); }, 350); }</script>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  };

  const printInvoice = async (invoice) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    let d = invoiceDetails;
    if (invoice) {
      d = await fetchInvoiceDetails(invoice);
    }
    if (!d) return;

    let patientDetails = {};
    try {
      const res = await axiosInstance.get(`getPatientById/${d.patientId || patientId}`);
      patientDetails = res.data?.data || {};
    } catch (_) {
      // proceed without extra patient details
    }

    const allItemsRows = [
      ...(d.services || []).map((s) => ({ ...s, type: "service" })),
      ...(d.inventory || []).map((i) => ({ ...i, type: "inventory" })),
    ]
      .map((item, idx) => {
        const date = item.date
          ? new Date(item.date).toLocaleDateString("en-GB")
          : "-";
        const priceDisplay =
          item.type === "inventory"
            ? `${Number(item.price || 0).toFixed(3)} x ${item.quantity || 1}`
            : Number(item.price || 0).toFixed(3);
        // `item.price` is already the single (unit) price; the Total column carries price x qty
        const net = item.type === "inventory" ? item.totalPrice : item.netAmount;

        return `<tr>
         <td style="border: 1px solid #000;">${idx + 1}</td>
         <td style="border: 1px solid #000;">${date}</td>
         <td style="border: 1px solid #000;">${item.code || "-"}</td>
         <td style="border: 1px solid #000;">${item.name || "-"}</td>
         <td style="border: 1px solid #000; text-align:right">${priceDisplay} BHD</td>
         <td style="border: 1px solid #000; text-align:right">${Number(
          item.discount || 0
        ).toFixed(3)} BHD</td>
         <td style="border: 1px solid #000; text-align:right">-</td>
         <td style="border: 1px solid #000; text-align:right">${Number(
          net || 0
        ).toFixed(3)} BHD</td>
       </tr>`;
      })
      .join("");

    const relatedPayments = (payments || []).filter(
      (p) =>
        String(p.invoice_id || p.invoiceId) === String(d.invoiceNo) ||
        String(p.invoice_no) === String(d.invoiceNo)
    );

    const paymentRows = relatedPayments
      .map((p, idx) => {
        const pd = p.payment_date || p.date || p.created_at;
        const dateStr = pd ? new Date(pd).toLocaleDateString("en-GB") : "-";
        return `<tr>
        <td style="border: 1px solid #000;">${idx + 1}</td>
        <td style="border: 1px solid #000;">${dateStr}</td>
        <td style="border: 1px solid #000;">${p.invoice_no || p.invoice_id || "-"
          }</td>
        <td style="border: 1px solid #000;">${p.payment_method || "-"}</td>
        <td style="border: 1px solid #000;">${p.transactionId || p.transaction_id || "-"
          }</td>
        <td style="border: 1px solid #000; text-align:right">${Number(
            p.payment_amount ?? p.amount ?? 0
          ).toFixed(3)} BHD</td>
      </tr>`;
      })
      .join("");

    const totalDiscount = [
      ...(d.services || []),
      ...(d.inventory || []),
    ].reduce((acc, curr) => acc + Number(curr.discount || 0), 0);

    const totalPayment = relatedPayments.reduce(
      (acc, curr) => acc + Number(curr.payment_amount ?? curr.amount ?? 0),
      0
    );

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
<title>Invoice ${d.invoiceNo}</title>
<style>
@page {
  size: A4;
  margin: 15mm 12mm 18mm 12mm;
}
* {
  box-sizing: border-box;
}
html, body {
  width: 100%;
  margin: 0;
  padding: 0;
}
body {
font-family: Arial, sans-serif;
line-height: 1.2;
}
table { page-break-inside: auto; }
tr { page-break-inside: avoid; page-break-after: auto; }
thead { display: table-header-group; }
tfoot { display: table-footer-group; }

.logo{
text-align:center}
.top-section {
display: flex;
justify-content: space-between;
margin-top: 20px;
}

.billingInvoice h2 {
font-size: 25px;
font-weight: 600;
}

.patient-info {
text-align: left;
font-size: 14px;
}

.invoice-title {
margin-top: 30px;
text-align: center;
}
.billingInvoice p,.billingInvoice h2 {
margin-top:0px;
margin-bottom:0px}
.paymentLine {
display:flex;
justify-content:end;
margin-top:50px;
}
.paymentLine p:first-child{
margin-right:2px;
padding-right:2px;
border-right:1px solid #000;
}
table th,table td{
text-align:left;
padding:4px 10px;
}
.terms strong{
margin-left:5px}
.footer{
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
}
</style>
</head>
<body>
<div class="billingInvoice">
<div class="logo">
<img height=100 src="${hospitalLogo}" />
</div>
<div class="top-section">
<div class="hospital-info">
<h2>${hospitalName}</h2>
<p><strong>CR:</strong> ${hospitalCR}</p>
<p><strong>VAT No.</strong> ${hospitalVAT}</p>
</div>

<div class="patient-info">
<p style="margin: 0; padding: 0;"><strong>Patient Name: </strong> ${d.patientName || billingSummary.patient_name
      }</p>
<p style="margin: 0; padding: 0;"><strong>Patient ID: </strong> ${patientDetails.fileNumber || d.patientId || patientId}</p>
<p style="margin: 0; padding: 0;"><strong>Mobile: </strong> ${patientDetails.mobileNumber || "-"}</p>
</div>
</div>
<div class="invoice-title">
<h2>Invoice # ${d.invoiceNo}</h2>
<p style="margin-top:20px"><strong>Date:</strong> ${new Date(
        d.date
      ).toLocaleDateString("en-GB")}</p>
</div>
</div>

<div class="paymentLine">
<p> <strong>Total Payment: <span style="color:#318965">${Number(
        totalPayment ?? 0
      ).toFixed(3)} BHD</span> </strong></p>
<p> <strong>Remaining Balance: <span style="color:#dc2626">${Number(
        d.remainingAmount ?? 0
      ).toFixed(3)} BHD</span> </strong></p>
</div>

<h5 style="margin-top:20px">Items & Services</h5>
<table style="width: 100%; border-collapse: collapse; font-size: 12px;">
<thead>
<tr>
<th style="border: 1px solid #000;">#</th>
<th style="border: 1px solid #000;">Date</th>
<th style="border: 1px solid #000;">Code</th>
<th style="border: 1px solid #000;">Name</th>
<th style="border: 1px solid #000;">Price</th>
<th style="border: 1px solid #000;">Discount</th>
<th style="border: 1px solid #000;">VAT</th>
<th style="border: 1px solid #000;">Total</th>
</tr>
</thead>

<tbody>
${allItemsRows || '<tr><td colspan="8" style="text-align:center; border:1px solid #000;">No items found</td></tr>'}
</tbody>

<tfoot>
<tr>
<td colspan="5" style="border: 1px solid #000; text-align: right;"><strong>Sum</strong></td>
<td style="border: 1px solid #000; text-align: right;"><strong>${totalDiscount.toFixed(
        3
      )}</strong></td>
<td style="border: 1px solid #000; text-align: right;"><strong>-</strong></td>
<td style="border: 1px solid #000; text-align: right;"><strong>${Number(
        d.total ?? 0
      ).toFixed(3)}</strong></td>
</tr>
</tfoot>
</table>


<h5 style="margin-top:30px">Payments Breakdown</h5>
<table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-top:10px">
<thead>
<tr>
<th style="border: 1px solid #000;">#</th>
<th style="border: 1px solid #000;">Date</th>
<th style="border: 1px solid #000;">INV</th>
<th style="border: 1px solid #000;">Method</th>
<th style="border: 1px solid #000;">TRN-ID</th>
<th style="border: 1px solid #000;">Amount</th>
</tr>
</thead>

<tbody>
${paymentRows || '<tr><td colspan="6" style="text-align:center; border:1px solid #000;">No payments recorded</td></tr>'}
</tbody>

<tfoot>
<tr>
<td colspan="5" style="border: 1px solid #000; text-align: right;"><strong>Total Paid</strong></td>
<td style="border: 1px solid #000; text-align: right;"><strong>${totalPayment.toFixed(
        3
      )} BHD</strong></td>
</tr>
</tfoot>
</table>

<h5 style="margin-top:50px">Terms & Conditions</h5>
<div style="margin-top:10px;padding:10px; border: 1px solid #eee; border-radius: 4px; font-size: 11px; white-space: pre-wrap;">
  ${termsCondition?.content || "Thank you for your business."}
</div>

</div>

<div class="footer">
        Hospital Name: ${hospitalName} | Email: ${email} | Phone: ${phone} | Website: ${website}
</div>

<script>
  window.onload = () => {
      setTimeout(() => {
          window.print();
      }, 500);

      window.onafterprint = () => {
          window.close();
      };
  };
</script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Secondary trigger if window.onload script doesn't fire as expected in some browsers
    printWindow.onload = () => {
      printWindow.focus();
    };
  };

  // const emailInvoice = async () => {
  //   if (!activeInvoice) return;
  //   const email = window.prompt("Enter recipient email:");
  //   if (!email) return;
  //   try {
  //     setEmailSending(true);
  //     const id =
  //       activeInvoice.id || activeInvoice.invoice_id || activeInvoice.invoiceNo;
  //     await axiosInstance.post(`sendInvoiceEmail`, { invoiceId: id, email });
  //     Swal.fire("Sent", "Invoice emailed successfully", "success");
  //   } catch (e) {
  //     const msg = e.response?.data?.message || "Failed to send email";
  //     Swal.fire("Error", msg, "error");
  //   } finally {
  //     setEmailSending(false);
  //   }
  // };

  const loadBillables = async () => {
    try {
      const [sRes, iRes] = await Promise.all([
        axiosInstance.get(`getPatientServicesByPatientId/${patientId}`),
        axiosInstance.get(`getPatientPharmacyByPatientId/${patientId}`),
      ]);

      const rawServices = Array.isArray(sRes.data?.data)
        ? sRes.data.data
        : Array.isArray(sRes.data)
          ? sRes.data
          : Array.isArray(sRes.data?.data?.results)
            ? sRes.data.data.results
            : [];

      const rawInventory = (
        Array.isArray(iRes.data?.data)
          ? iRes.data.data
          : Array.isArray(iRes.data)
            ? iRes.data
            : []
      ).map((x) => {
        const unitPrice = Number(x.unitPrice ?? x.price ?? 0);
        const qty = Number(x.quantity ?? x.qty ?? 1);
        return {
          id: x.id || x.itemId || x.pharmacyId,
          name: x.drug_name || x.name || `Medicine #${x.drugId || x.id}`,
          code: x.drug_code || x.code || x.drugId || x.id,
          price: unitPrice,
          quantity: qty,
          totalPrice: unitPrice * qty,
          billed:
            x.billed ??
            x.isBilled ??
            x.is_billed ??
            (x.billing_status === "Billed" ? 1 : 0),
        };
      });

      // Include services with status Completed which are NOT billed yet
      const eligibleServices = rawServices.filter((x) => {
        const status = (x.status || x.Status || "").toString().toLowerCase();
        const billedFlag =
          x.billed ??
          x.isBilled ??
          x.is_billed ??
          ((x.billing_status || "").toString().toLowerCase() === "billed"
            ? 1
            : 0);
        return status === "complete" && String(billedFlag) !== "1";
      });

      const notBilledInventory = rawInventory.filter(
        (x) => String(x.billed) !== "1"
      );

      setServices(eligibleServices);
      setInventory(notBilledInventory);
    } catch (_) { }
  };

  const toggleService = (id) => {
    setSelected((prev) => ({
      ...prev,
      services: { ...prev.services, [id]: !prev.services[id] },
    }));
  };

  const toggleInventory = (id) => {
    setSelected((prev) => ({
      ...prev,
      inventory: { ...prev.inventory, [id]: !prev.inventory[id] },
    }));
  };

  const toggleLab = (id) => {
    setSelected((prev) => ({
      ...prev,
      labs: { ...prev.labs, [id]: !prev.labs[id] },
    }));
  };

  const chosenServices = useMemo(
    () => services.filter((s) => selected.services[s.id]),
    [services, selected.services]
  );
  const chosenInventory = useMemo(
    () => inventory.filter((i) => selected.inventory[i.id]),
    [inventory, selected.inventory]
  );
  const chosenLabs = useMemo(
    () => labRequests.filter((l) => selected.labs[l.id]),
    [labRequests, selected.labs]
  );

  const totalAmount = useMemo(() => {
    const s = chosenServices.reduce(
      (sum, x) => sum + (x.netAmount ?? x.net_amount ?? 0),
      0
    );
    const i = chosenInventory.reduce(
      (sum, x) => sum + (x.totalPrice ?? x.total_price ?? 0),
      0
    );
    const l = chosenLabs.reduce((sum, x) => {

      const servicesTotal = (x.services || []).reduce(
        (acc, s) => acc + (Number(s.price) || 0),
        0
      );
      return sum + (servicesTotal > 0 ? servicesTotal : Number(x.price) || 0);
    }, 0);
    return s + i + l;
  }, [chosenServices, chosenInventory, chosenLabs]);

  const nextInvoiceNo = useMemo(() => {
    const prefix = "INV";
    let max = 0;
    invoices.forEach((inv) => {
      const str = inv.invoiceNo || inv.invoice_id || inv.id || "";
      const m = (str || "").toString().match(/(\d+)/);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    const next = (max || 0) + 1;
    return `${prefix}${String(next).padStart(6, "0")}`;
  }, [invoices]);

  // Pagination calculations for invoices
  const filteredInvoices = useMemo(() => {
    if (!invoiceSearchTerm) return invoices;
    const q = invoiceSearchTerm.toLowerCase();
    return invoices.filter(
      (inv) =>
        (inv.invoice_no && inv.invoice_no.toLowerCase().includes(q)) ||
        (inv.invoiceNo && inv.invoiceNo.toLowerCase().includes(q)) ||
        (inv.id && inv.id.toString().toLowerCase().includes(q)) ||
        (inv.total_amount && inv.total_amount.toString().includes(q)) ||
        (inv.paid_amount && inv.paid_amount.toString().includes(q)) ||
        (inv.remaining_amount && inv.remaining_amount.toString().includes(q)) ||
        (inv.invoice_status && inv.invoice_status.toLowerCase().includes(q)) ||
        (inv.status && inv.status.toLowerCase().includes(q))
    );
  }, [invoices, invoiceSearchTerm]);

  const currentInvoices = useMemo(() => {
    const startIndex = (currentInvoicePage - 1) * invoicesPerPage;
    const endIndex = startIndex + invoicesPerPage;
    return filteredInvoices.slice(startIndex, endIndex);
  }, [filteredInvoices, currentInvoicePage, invoicesPerPage]);

  const totalInvoicePages = useMemo(() => {
    return Math.ceil(filteredInvoices.length / invoicesPerPage);
  }, [filteredInvoices, invoicesPerPage]);

  // Pagination calculations for payments
  const filteredPayments = useMemo(() => {
    if (!paymentSearchTerm) return payments;
    const q = paymentSearchTerm.toLowerCase();
    return payments.filter(
      (pay) =>
        (pay.invoice_no && pay.invoice_no.toLowerCase().includes(q)) ||
        (pay.invoice_id &&
          pay.invoice_id.toString().toLowerCase().includes(q)) ||
        (pay.invoiceId && pay.invoiceId.toString().toLowerCase().includes(q)) ||
        (pay.payment_amount && pay.payment_amount.toString().includes(q)) ||
        (pay.amount && pay.amount.toString().includes(q)) ||
        (pay.payment_method && pay.payment_method.toLowerCase().includes(q)) ||
        (pay.method && pay.method.toLowerCase().includes(q)) ||
        (pay.transactionId && pay.transactionId.toLowerCase().includes(q)) ||
        (pay.transaction_id && pay.transaction_id.toLowerCase().includes(q))
    );
  }, [payments, paymentSearchTerm]);

  const currentPayments = useMemo(() => {
    const startIndex = (currentPaymentPage - 1) * paymentsPerPage;
    const endIndex = startIndex + paymentsPerPage;
    return filteredPayments.slice(startIndex, endIndex);
  }, [filteredPayments, currentPaymentPage, paymentsPerPage]);

  const totalPaymentPages = useMemo(() => {
    return Math.ceil(filteredPayments.length / paymentsPerPage);
  }, [filteredPayments, paymentsPerPage]);

  // Searchable select options for services & inventory
  const serviceOptions = useMemo(
    () =>
      (services || []).map((s) => ({
        value: s.id,
        label: `${s.service_name} ${s.service_code || s.code ? `(${s.service_code || s.code})` : ""
          }`.trim(),
        data: s, // Store full service data
      })),
    [services]
  );
  const inventoryOptions = useMemo(
    () =>
      (inventory || []).map((i) => ({
        value: i.id,
        label: `${i.name} ${i.code ? `(${i.code})` : ""}`.trim(),
        data: i, // Store full inventory data
      })),
    [inventory]
  );

  const labOptions = useMemo(
    () =>
      (labRequests || []).map((l) => {
        const services = l.services || [];
        const serviceNames = services.map((s) => s.name).join(", ");
        const nameLabel =
          serviceNames ||
          l.serviceNames ||
          l.test_name ||
          l.title ||
          l.name ||
          "Lab Request";

        //use net_amount instead of calculating from services
        const effectivePrice = parseFloat(l.net_amount || 0);

        // const price = services.reduce(
        //   (sum, s) => sum + (Number(s.price) || 0),
        //   0
        // );
        // const effectivePrice = price > 0 ? price : Number(l.price) || 0;

        return {
          value: l.id,
          label: `${nameLabel} (${effectivePrice.toFixed(3)} BHD)`,
          data: l,
        };
      }),
    [labRequests]
  );

  // Get selected values for multi-select
  const selectedServiceOptions = useMemo(
    () => serviceOptions.filter((opt) => selected.services[opt.value]),
    [serviceOptions, selected.services]
  );
  const selectedInventoryOptions = useMemo(
    () => inventoryOptions.filter((opt) => selected.inventory[opt.value]),
    [inventoryOptions, selected.inventory]
  );
  const selectedLabOptions = useMemo(
    () => labOptions.filter((opt) => selected.labs[opt.value]),
    [labOptions, selected.labs]
  );

  const handleServiceSearchSelect = (selectedOptions) => {
    // selectedOptions is an array when isMulti is true
    const newSelected = {};
    if (selectedOptions && selectedOptions.length > 0) {
      selectedOptions.forEach((opt) => {
        newSelected[opt.value] = true;
      });
    }
    setSelected((prev) => ({
      ...prev,
      services: newSelected,
    }));
  };
  const handleInventorySearchSelect = (selectedOptions) => {
    // selectedOptions is an array when isMulti is true
    const newSelected = {};
    if (selectedOptions && selectedOptions.length > 0) {
      selectedOptions.forEach((opt) => {
        newSelected[opt.value] = true;
      });
    }
    setSelected((prev) => ({
      ...prev,
      inventory: newSelected,
    }));
  };

  const handleLabSearchSelect = (selectedOptions) => {
    const newSelected = {};
    if (selectedOptions && selectedOptions.length > 0) {
      selectedOptions.forEach((opt) => {
        newSelected[opt.value] = true;
      });
    }
    setSelected((prev) => ({
      ...prev,
      labs: newSelected,
    }));
  };

  const handleNewInvoice = () => {
    loadBillables();
    setIsOpen(true);
  };

  const createInvoice = async () => {
    if (
      chosenServices.length === 0 &&
      chosenInventory.length === 0 &&
      chosenLabs.length === 0
    ) {
      Swal.fire(
        "Select items",
        "Please select at least one service, inventory item or lab request.",
        "info"
      );
      return;
    }
    try {
      setLoading(true);

      const payload = {
        patientId,
        services: chosenServices.map((x) => x.id),
        pharmacy: chosenInventory.map((x) => x.id),
        lab: chosenLabs.map((x) => x.id),
        branch_id: invoiceBranchId || null,
      };

      const res = await axiosInstance.post(`createInvoice`, payload);
      const created = res?.data?.data || null;

      Swal.fire(
        "Success",
        `Invoice ${created?.invoice_no || created?.invoiceNo || "created"
        } created successfully`,
        "success"
      );

      setIsOpen(false);
      setSelected({ services: {}, inventory: {}, labs: {} });

      // Notify globally that an invoice was created (for same patient)
      try {
        EventBus.dispatch("invoice:created", { patientId, invoice: created });
      } catch (_) { }

      await Promise.all([
        loadInvoices(),
        loadBillables(),
        loadLabRequests(),
        loadBillingSummary(),
      ]);
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to create invoice";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleShowDateRangeDialog = () => {
    setShowDateRangeDialog(true);
  };

  const handlePrintSummary = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      Swal.fire("Error", "Please select both start and end dates", "error");
      return;
    }
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      Swal.fire("Error", "Start date cannot be after end date", "error");
      return;
    }
    // Close the modal immediately so the screen is not blocked while generating
    setShowDateRangeDialog(false);

    // Fetch invoices within the selected date range using body parameters
    await fetchInvoiceSummary(dateRange.startDate, dateRange.endDate);
  };

  const sentEmailSummary = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      Swal.fire("Error", "Please select both start and end dates", "error");
      return;
    }
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      Swal.fire("Error", "Start date cannot be after end date", "error");
      return;
    }

    try {
      setEmailSending(true);

      // 1. Fetch the data first
      const summaryRes = await axiosInstance.post(`getInvoiceSummary`, {
        patientId: parseInt(patientId),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      if (!summaryRes.data.success) {
        throw new Error(
          summaryRes.data.message || "Failed to fetch invoice data"
        );
      }

      // 2. Generate HTML
      const htmlContent = printInvoiceSummary(
        summaryRes.data.data,
        dateRange.startDate,
        dateRange.endDate,
        true
      );

      // 3. Convert HTML to PDF using html2pdf
      const { default: html2pdf } = await import("html2pdf.js");

      const opt = {
        margin: 10,
        filename: "invoice_summary.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      const pdfBlob = await html2pdf()
        .set(opt)
        .from(htmlContent)
        .outputPdf("blob");
      const file = new File([pdfBlob], "invoice_summary.pdf", {
        type: "application/pdf",
      });

      // 4. Send File
      const formData = new FormData();
      formData.append("patientId", parseInt(patientId));
      formData.append("file", file);

      const res = await axiosInstance.post(`sendPatientFile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        Swal.fire("Success", "Email sent successfully", "success");
        setShowDateRangeDialog(false);
      } else {
        Swal.fire("Error", res.data.message || "Failed to send email", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire(
        "Error",
        error.message || "Something went wrong while sending email",
        "error"
      );
    } finally {
      setEmailSending(false);
    }
  };

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchInvoiceSummary = async (startDate, endDate) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`getInvoiceSummary`, {
        patientId: parseInt(patientId),
        startDate: startDate,
        endDate: endDate,
      });

      if (response.data.success) {
        printInvoiceSummary(response.data.data, startDate, endDate);
      } else {
        throw new Error(
          response.data.message || "Failed to fetch invoice summary"
        );
      }
    } catch (error) {
      console.error("Error fetching invoice summary:", error);
      Swal.fire("Error", "Failed to fetch invoice summary", "error");
    } finally {
      setLoading(false);
    }
  };

  // print invoices

  const printInvoiceSummary = (
    summaryData,
    startDate,
    endDate,
    returnHtml = false
  ) => {
    const { invoices = [], payments = [], patientData = [] } = summaryData;

    // If not returning HTML, open window immediately (though we could arguably wait, but existing logic did this)
    // Actually, we can just generate string first.
    // const printWindow = window.open("", "_blank");
    // if (!printWindow) return;

    // totals based on invoice list
    const totalInvoiced = invoices.reduce(
      (sum, inv) => sum + (Number(inv.total_amount) || 0),
      0
    );
    const totalPaid = invoices.reduce(
      (sum, inv) => sum + (Number(inv.paid_amount) || 0),
      0
    );
    const totalRemaining = invoices.reduce(
      (sum, inv) => sum + (Number(inv.patient_remaining ?? inv.remaining_amount) || 0),
      0
    );
    const totalInsuranceOutstanding = invoices.reduce(
      (sum, inv) => sum + (Number(inv.outstanding_insurance) || 0),
      0
    );

    const invoiceRows = (invoices || [])
      .map((inv, idx) => {
        const dateStr = inv.invoice_date
          ? new Date(inv.invoice_date).toLocaleDateString("en-GB")
          : "-";

        // Use API values directly
        const totalNum = Number(inv.total_amount || 0);
        const paidNum = Number(inv.paid_amount || 0);
        const remainingPatientNum = Number(inv.patient_remaining ?? inv.remaining_amount ?? 0);

        // Format for display
        const total = totalNum.toFixed(3);
        const paid = paidNum.toFixed(3);
        const remainingPatientAmt = remainingPatientNum.toFixed(3);

        // Calculate VAT dynamically from services
        let vat = "0%";
        if (inv.services && inv.services.length > 0) {
          // Get the VAT value from the first service (assuming all services in an invoice have the same VAT)
          const serviceWithVat = inv.services.find(
            (service) => service.vat_value !== undefined
          );
          if (serviceWithVat) {
            vat = `${Number(serviceWithVat.vat_value).toFixed(2)}%`;
          } else {
            vat = "0%";
          }
        }

        const allItems = [
          ...(inv.services || []),
          ...(inv.labs || []),
          ...(inv.pharmacy || [])
        ];
        const allServiceNames = allItems
          .map(item => item.serviceName || item.name || item.drug_name || "")
          .filter(name => name);
        const serviceNames = allServiceNames.length > 0 ? allServiceNames.join(", ") : "";

        const itemQty = allItems.length > 0 ? allItems.length : 1;
        const unitPrice = totalNum.toFixed(3);

        return `
        <tr>
          <td style="border: 1px solid #000;">${idx + 1}</td>
          <td style="border: 1px solid #000;">${dateStr}</td>
          <td style="border: 1px solid #000;">${inv.invoice_no || ""}</td>
          <td style="border: 1px solid #000;">${serviceNames}</td>
          <td style="border: 1px solid #000; text-align:right;">${unitPrice}</td>
          <td style="border: 1px solid #000; text-align:right;">${itemQty}</td>
          <td style="border: 1px solid #000; text-align:right;">${remainingPatientAmt}</td>
          <td style="border: 1px solid #000; text-align:right;">${vat}</td>
          <td style="border: 1px solid #000; text-align:right;">${total}</td>
        </tr>`;
      })
      .join("");

    const paymentRows = (payments || [])
      .map((pay, idx) => {
        const dateStr = pay.payment_date
          ? new Date(pay.payment_date).toLocaleDateString("en-GB")
          : "-";
        return `
        <tr>
          <td style="border: 1px solid #000;">${idx + 1}</td>
          <td style="border: 1px solid #000;">${dateStr}</td>
          <td style="border: 1px solid #000;">${pay.invoice_id || ""}</td>
          <td style="border: 1px solid #000;">${pay.payment_method || "-"}</td>
          <td style="border: 1px solid #000;">${pay.transactionId || pay.transaction_id || "-"
          }</td>
          <td style="border: 1px solid #000; text-align:right;">${Number(
            pay.payment_amount || 0
          ).toFixed(3)}</td>
        </tr>`;
      })
      .join("");

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
<title>VAT Invoice</title>
<style>
  @page {
  size: auto;
  margin: 5mm; /* bottom margin for footer */
}
body {
  font-family: Arial, sans-serif;
  line-height: 1.2;
  margin:30px;
}

.logo {
  text-align: center;
}

.top-section {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.billingInvoice h2 {
  font-size: 25px;
  font-weight: 600;
}

.patient-info {
  text-align: left;
  font-size: 14px;
}

.invoice-title {
  margin-top: 30px;
  text-align: center;
}

.billingInvoice p,
.billingInvoice h2 {
  margin-top: 0px;
  margin-bottom: 0px;
}

.paymentLine {
  display: flex;
  justify-content: end;
  margin-top: 50px;
}

.paymentLine p {
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid #000;
}
.paymentLine p:last-child{
border:unset;
}
table th,
table td {
  text-align: left;
  padding: 2px 10px;
  font-size:14px
}
.top-section p,.paymentLine p{
font-size:14px
}

.termsACon li{
font-size:12px;
}
 .termsACon ul{
 padding-left:12px;
 }

.footer {
  position: fixed;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #000;
  bottom:0px;
}

</style>
</head>
<body>
<div class="billingInvoice">
  <div class="logo">
    <img height="80" src="${hospitalLogo}" />
  </div>
  <div class="top-section">
    <div class="hospital-info">
      <p><strong>${hospitalName}</strong></p>
      <p><strong>CR:</strong> ${hospitalCR}</p>
      <p><strong>VAT No.</strong> ${hospitalVAT}</p>
    </div>

    <div class="patient-info">
    <div>
    <strong>Patient Name: </strong> ${patientData.patient_name || ""}
    </div>
    <div>
    <strong>CPR: </strong> ${patientData.civilIdNumber || ""}
    <strong>Mobile: </strong> ${patientData.mobileNumber || ""}
    </div>
    <div>
    <p><strong>Date Range:</strong> ${new Date(startDate).toLocaleDateString(
      "en-GB"
    )} to ${new Date(endDate).toLocaleDateString("en-GB")}</p>
     </div>
    </div>
  </div>
  <div class="invoice-title">
    <h2>VAT Invoice</h2>
    <p style="margin-top:20px"><strong>Generated on:</strong> ${new Date().toLocaleDateString(
      "en-GB"
    )}</p>
  </div>
</div>

<div class="paymentLine">
  <p> <strong>Total Invoiced: <span style="color:#318965">${totalInvoiced.toFixed(3)}</span> </strong></p>
  <p> <strong>Total Paid: <span style="color:#318965">${totalPaid.toFixed(3)}</span> </strong></p>
  ${totalInsuranceOutstanding > 0 ? `<p> <strong>Insurance Outstanding: <span style="color:#e58a00">${totalInsuranceOutstanding.toFixed(3)}</span> </strong></p>` : ''}
  <p> <strong>Patient Remaining: <span style="color:#dc2626">${totalRemaining.toFixed(3)}</span> </strong></p>
</div>

<table style="width: 100%; border-collapse: collapse;">
  <thead>
    <tr>
      <th style="border: 1px solid #000;">#</th>
      <th style="border: 1px solid #000;">Date</th>
      <th style="border: 1px solid #000;">Code</th>
      <th style="border: 1px solid #000;">Service Name</th>
      <th style="border: 1px solid #000;">Price</th>
      <th style="border: 1px solid #000;">Qty</th>
      <th style="border: 1px solid #000;">Rem.</th>
      <th style="border: 1px solid #000;">VAT</th>
      <th style="border: 1px solid #000;">Total</th>
    </tr>
  </thead>

  <tbody>
    ${invoiceRows}
  </tbody>

  <tfoot>
    <tr>
      <td colspan="6" style="border: 1px solid #000; text-align: right;"><strong>Sum</strong></td>
      <td style="border: 1px solid #000; text-align: right;"><strong>${(
        totalInvoiced - totalPaid
      ).toFixed(3)}</strong></td>
      <td style="border: 1px solid #000; text-align: right;"><strong>${(() => {
        // Calculate total VAT from all invoices
        let totalVat = 0;
        invoices.forEach((inv) => {
          if (inv.services && inv.services.length > 0) {
            const serviceWithVat = inv.services.find(
              (service) => service.vat_value !== undefined
            );
            if (serviceWithVat) {
              totalVat += Number(serviceWithVat.vat_value);
            }
          }
        });
        return totalVat > 0 ? `${totalVat.toFixed(2)}%` : "0%";
      })()}</strong></td>
      <td style="border: 1px solid #000; text-align: right;"><strong>${totalInvoiced.toFixed(
        3
      )}</strong></td>
    </tr>
  </tfoot>
</table>


<h5 style="margin-top:50px">Payments</h5>
<table style="width: 100%; border-collapse: collapse; font-size: 14px; margin-top:10px">
  <thead>
    <tr>
      <th style="border: 1px solid #000;">#</th>
      <th style="border: 1px solid #000;">Date</th>
      <th style="border: 1px solid #000;">INV</th>
      <th style="border: 1px solid #000;">Method</th>
      <th style="border: 1px solid #000;">TRN-ID</th>
      <th style="border: 1px solid #000;">Amount</th>
    </tr>
  </thead>

  <tbody>
    ${paymentRows}
  </tbody>

  <tfoot>
    <tr>
      <td colspan="4" style=" text-align: right;"><strong>Sum</strong></td>
      <td style="border: 1px solid #000; text-align: right;"></td>
      <td style="border: 1px solid #000; text-align: right;"><strong>${payments
        .reduce((sum, p) => sum + (Number(p.payment_amount) || 0), 0)
        .toFixed(3)}</strong></td>
    </tr>
  </tfoot>
</table>
<div class="termsACon">
<h5 style="margin-top:20px">Terms & Conditions</h5>
<div style="margin-top:10px;">
<ul>
<li>${termsCondition?.content || ""}</li>
</ul>

</div>
</div>



    <div class="footer">
      <div font-size: 12px; text-align: center; color: rgb(0, 0, 0);">
        Hospital Name: ${hospitalName} | Email:
        <span style="border-bottom: 1px solid rgb(44, 100, 223); color: rgb(44, 100, 223);">
         ${email}
        </span>
        | Phone: ${phone} |
        <span style="border-bottom: 1px solid rgb(44, 100, 223); color: rgb(44, 100, 223);">
          Website: ${website}
        </span>
      </div>
    </div>

<script>
  window.onload = () => {
    setTimeout(() => window.print(), 200);
  };
  window.onafterprint = () => window.close();
</script>

</body>
</html>`;

    if (returnHtml) {
      return htmlContent;
    }

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  };

  const handleDeleteInvoice = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axiosInstance.delete(`deleteInvoiceById/${id}`);
          if (response.data.success) {
            Swal.fire("Deleted!", "Your invoice has been deleted.", "success");
            loadInvoices();
            loadRemainingInvoices();
            loadBillingSummary();
            loadBillables();
            loadLabRequests();
          } else {
            Swal.fire(
              "Error!",
              response.data.message || "Something went wrong.",
              "error"
            );
          }
        } catch (error) {
          Swal.fire(
            "Error!",
            error.response?.data?.message || "Something went wrong.",
            "error"
          );
        }
      }
    });
  };

  return (
    <div className="col-12">
      <div className="patientCardHeader">
        <div className="row">
          {/* Top summary cards (moved from side Summary panel) */}
          <div className="col-12 mb-3">
            <div className="row g-3">
              <div className="col-md-3">
                <div className="cardBorder serviceCard h-100 p20">
                  <div className="d-flex justify-content-between">
                    <div className="pe-3">
                      <h6>Pending Balance</h6>

                      <h5>{billingSummary.pendingBalance.toFixed(3)} BHD</h5>
                    </div>
                    <div className="avtar avtar-s orangeBgDash">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          stroke="#e58a00"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 7v5l3 2"
                          stroke="#e58a00"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9 17h6"
                          stroke="#e58a00"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="cardBorder serviceCard h-100 p20">
                  <div className="d-flex justify-content-between">
                    <div className="pe-3">
                      <h6>Total Payments</h6>

                      <h5> {billingSummary.totalPayments.toFixed(3)} BHD</h5>
                    </div>
                    <div className="avtar avtar-s bg-light-success">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="3"
                          y="5"
                          width="18"
                          height="14"
                          rx="2"
                          stroke="#2ca87f"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3 10h18"
                          stroke="#2ca87f"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                        <circle cx="8" cy="15" r="1.5" fill="#2ca87f" />
                        <circle cx="12" cy="15" r="1.5" fill="#2ca87f" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="cardBorder serviceCard h-100 p20">
                  <div className="d-flex justify-content-between">
                    <div className="pe-3">
                      <h6>Total Billed</h6>

                      <h5> {billingSummary.totalBilled.toFixed(3)} BHD</h5>
                    </div>
                    <div className="avtar avtar-s bg-light-primary ">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M6 3h12a1 1 0 0 1 1 1v16l-4-2-4 2-4-2-4 2V4a1 1 0 0 1 1-1z"
                          stroke="#4680ff"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 8h8M8 12h5"
                          stroke="#4680ff"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="cardBorder serviceCard h-100 p20">
                  <div className="d-flex justify-content-between">
                    <div className="pe-3">
                      <h6> Insurance Balance</h6>

                      <h5> {InsAmount.toFixed(3)} BHD</h5>
                    </div>
                    <div className="avtar avtar-s yellowIcon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2l7 4v6c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-4z"
                          stroke="#daa300"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 8v4"
                          stroke="#daa300"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="15.5" r="0.8" fill="#daa300" />
                      </svg>
                    </div>
                  </div>
                  {/* <div className="text-muted small mt-1">
                    Last Payment:{" "}
                    {billingSummary.lastPaymentDate
                      ? new Date(
                          billingSummary.lastPaymentDate
                        ).toLocaleDateString("en-GB")
                      : ""}
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            {/* Invoices Panel */}
            <div className="cardBorder">
              <div className="tableHeader d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <h5 className="mb-0">Invoices</h5>
                  <div
                    className="tableSearch"
                    style={{ position: "relative", width: "250px" }}
                  >
                    <input
                      type="text"
                      placeholder="Search invoices..."
                      className="form-control"
                      value={invoiceSearchTerm}
                      onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                      style={{ paddingLeft: "35px" }}
                    />
                    <span className="searchIcon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="gray"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "10px",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  {/* <button
                    className="invoiceHeaderIcon"
                    title="Settings"
                  >
                    <i className="ti ti-settings"></i>
                  </button> */}
                  {/* <button
                    className="invoiceHeaderIcon"
                    title="Filter"
                  >
                    <i className="ti ti-filter"></i>
                  </button> */}
                  <div className="d-flex gap-2">
                    <button
                      className="bgBtn btn-sm d-flex align-items-center gap-1"
                      style={{ fontSize: 13, padding: "5px 12px" }}
                      onClick={() => setIsOpen(true)}
                      disabled={!patientId || loading}
                    >
                      <i className="fas fa-plus"></i>
                      Create Invoice
                    </button>
                    <button
                      className="bgBtn btn-sm d-flex align-items-center gap-1"
                      style={{ fontSize: 13, padding: "5px 12px" }}
                      onClick={handleShowDateRangeDialog}
                      disabled={!patientId || loading}
                      title="Print Invoice Summary"
                    >
                      <i className="fas fa-print"></i>
                      Print Summary
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="">Inv#</th>
                        <th className="">Date</th>
                        <th className="">Services</th>
                        <th className="">Total Amount</th>
                        <th className="">Paid Amount</th>
                        <th className="">VAT%</th>
                        <th className="">VAT Amount</th>
                        <th className="">Remaining</th>
                        {/* <th className="">Discount</th> */}
                        {/* <th className="">Insurance</th> */}
                        <th>Added By</th>
                        <th className="">Status</th>
                        <th className=" text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentInvoices.length === 0 ? (
                        <tr>
                          <td
                            colSpan="10"
                            className="text-center text-muted py-4"
                          >
                            No invoices found
                          </td>
                        </tr>
                      ) : (
                        currentInvoices.map((invoice, index) => {
                          const invoiceLocked = recordLockHours != null && (() => {
                            const ts = invoice.invoice_date
                            if (!ts) return false
                            return (Date.now() - new Date(ts).getTime()) > recordLockHours * 3600 * 1000
                          })()
                          return (<tr key={index}>
                            <td>
                              {invoice.invoice_no ||
                                invoice.invoiceNo ||
                                invoice.id ||
                                `INV${index + 1}`}
                            </td>
                            <td>
                              {(() => {
                                const ts =
                                  invoice.invoice_date ||
                                  invoice.updated_at ||
                                  invoice.updatedAt ||
                                  invoice.date;
                                const d = ts ? new Date(ts) : null;
                                return d && !isNaN(d.getTime())
                                  ? d.toLocaleDateString("en-GB")
                                  : "-";
                              })()}
                            </td>
                            <td className="wrapText">
                              {[
                                ...(invoice.services || []),
                                ...(invoice.labs || []),
                                ...(invoice.pharmacy || []),
                              ].length > 0
                                ? [
                                  ...(invoice.services || []),
                                  ...(invoice.labs || []),
                                  ...(invoice.pharmacy || []),
                                ]
                                  .map((s) => {
                                    const label = s.serviceName || s.name || s.drug_name || "";
                                    const qty = Number(s.quantity ?? 1);
                                    return qty > 1 ? `${label} (x${qty})` : label;
                                  })
                                  .join(", ")
                                : "No Services"}
                            </td>
                            <td>
                              {Number(invoice.total_amount || 0).toFixed(3)} BHD
                            </td>
                            <td>
                              {Number(invoice.paid_amount || 0).toFixed(3)} BHD
                            </td>
                            <td>
                              {(() => {
                                const allItems = [
                                  ...(invoice.services || []),
                                  ...(invoice.pharmacy || []),
                                ];
                                const totalVat = allItems.reduce(
                                  (sum, s) => sum + Number(s.vat_value || 0),
                                  0
                                );
                                const base = Number(invoice.total_amount || 0) - totalVat;
                                if (totalVat === 0 || base <= 0) return "0%";
                                const pct = (totalVat / base) * 100;
                                return `${pct.toFixed(2)}%`;
                              })()}
                            </td>
                            <td>
                              {(() => {
                                const allItems = [
                                  ...(invoice.services || []),
                                  ...(invoice.pharmacy || []),
                                ];
                                const totalVat = allItems.reduce(
                                  (sum, s) => sum + Number(s.vat_value || 0),
                                  0
                                );
                                return `${totalVat.toFixed(3)} BHD`;
                              })()}
                            </td>
                            <td>
                              {invoice.remaining_amount != null
                                ? `${Number(invoice.remaining_amount).toFixed(
                                  3
                                )} BHD`
                                : "-"}
                            </td>

                            <td>
                              {invoice.sent_by || invoice.created_by || "-"}
                            </td>
                            {/* <td>{invoice.discount ?? "-"}</td> */}
                            {/* <td>{invoice.insurance ?? "No"}</td> */}
                            <td>
                              <span
                                className={`badge ${(() => {
                                  const status = (
                                    invoice.invoice_status ||
                                    invoice.status ||
                                    ""
                                  )
                                    .toString()
                                    .toLowerCase();
                                  if (
                                    status === "fully paid" ||
                                    status === "paid"
                                  ) {
                                    return "bg-success-subtle text-success";
                                  } else if (
                                    status === "not paid" ||
                                    status === "unpaid"
                                  ) {
                                    return "bg-danger-subtle text-danger";
                                  } else {
                                    return "bg-warning-subtle text-warning";
                                  }
                                })()} fw-normal px-3 py-2 rounded-pill`}
                              >
                                {invoice.invoice_status ||
                                  invoice.status ||
                                  "-"}
                              </span>
                            </td>
                            <td className="text-center">
                              {/* <button
                                onClick={() => openInvoiceDetail(invoice)}
                                className="avtar avtar-xs btn-link-secondary viewIcon"
                              >
                                <i className="ti ti-eye f-20" />
                                <span>View</span>
                              </button> */}

                              <button
                                className="btn btn-link p-0 text-decoration-none me-2"
                                title="Pay"
                                onClick={() => openPaymentForInvoice(invoice)}
                              >
                                {/* <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="feather feather-credit-card text-success"
                                >
                                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                  <line x1="1" y1="10" x2="23" y2="10"></line>
                                </svg> */}
                              </button>

                              <div
                                style={{ cursor: "pointer" }}
                                className="avtar avtar-xs btn-link-secondary viewIcon"
                                onClick={() =>
                                  navigate(
                                    `/Admin/ViewInvoice/${invoice.id ||
                                    invoice.invoice_id ||
                                    invoice.invoiceId ||
                                    invoice.invoice_no
                                    }`,
                                    { state: { invoice } }
                                  )
                                }
                              >
                                <i className="ti ti-eye f-20" />
                                {/* <span>View</span> */}
                              </div>

                              <div
                                style={{ cursor: "pointer" }}
                                className="avtar avtar-xs btn-link-secondary"
                                title="Print"
                                onClick={() => handlePrintInvoiceRow(invoice)}
                              >
                                <i className="ti ti-printer f-20" />
                              </div>

                              <button
                                className="avtar avtar-xs btn-link-secondary deleteIcon"
                                title={
                                  invoiceLocked
                                    ? "Record is locked"
                                    : invoice.invoice_status === "Not Paid"
                                    ? "Delete"
                                    : "Cannot delete paid invoice"
                                }
                                disabled={invoice.invoice_status !== "Not Paid" || invoiceLocked}
                                style={{
                                  cursor:
                                    invoice.invoice_status !== "Not Paid" || invoiceLocked
                                      ? "not-allowed"
                                      : "pointer",
                                  opacity:
                                    invoice.invoice_status !== "Not Paid" || invoiceLocked
                                      ? 0.5
                                      : 1,
                                }}
                                onClick={() =>
                                  handleDeleteInvoice(
                                    invoice.id ||
                                    invoice.invoice_id ||
                                    invoice.invoiceId
                                  )
                                }
                              >
                                <i className="ti ti-trash f-20" />
                              </button>
                            </td>
                          </tr>
                        )})
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination for Invoices */}
                {/* {totalInvoicePages > 1 && (
                  <div className="d-flex justify-content-end mt-3 m-b-2">
                    <Pagination
                      currentPage={currentInvoicePage}
                      totalPages={totalInvoicePages}
                      setCurrentPage={setCurrentInvoicePage}
                    />
                  </div>
                )} */}

                {totalInvoicePages > 1 && (
                  <nav className="tablepagination d-flex justify-content-end mt-3 mb-2">
                    <ul className="pagination mb-0">
                      <li
                        className={`page-item ${currentInvoicePage === 1 ? "disabled" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            currentInvoicePage > 1 &&
                            setCurrentInvoicePage(currentInvoicePage - 1)
                          }
                        >
                          &laquo;
                        </button>
                      </li>

                      <li className="pageNmbr">{`${currentInvoicePage} of ${totalInvoicePages}`}</li>

                      <li
                        className={`page-item ${currentInvoicePage === totalInvoicePages
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            currentInvoicePage < totalInvoicePages &&
                            setCurrentInvoicePage(currentInvoicePage + 1)
                          }
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>

            {/* Payment Invoices Panel */}
            <div
              className="cardBorder
             mt-3"
            >
              <div className="tableHeader">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <h5 className="mb-0">Payment Transactions</h5>
                    <div
                      className="tableSearch"
                      style={{ position: "relative", width: "250px" }}
                    >
                      <input
                        type="text"
                        placeholder="Search payments..."
                        className="form-control"
                        value={paymentSearchTerm}
                        onChange={(e) => setPaymentSearchTerm(e.target.value)}
                        style={{ paddingLeft: "35px" }}
                      />
                      <span className="searchIcon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="gray"
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "10px",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                          }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <button
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ fontSize: 13, padding: "5px 12px" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#ACF39D"; e.currentTarget.style.borderColor = "#ACF39D"; e.currentTarget.style.color = "#1a1f36"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = ""; e.currentTarget.style.color = ""; }}
                    onClick={openPaymentBlank}
                  >
                    Payment
                  </button>
                </div>
              </div>
              <div>
                <div className="table-responsive w-100">
                  <table className="table w-100">
                    <thead>
                      <tr>
                        <th>Sn</th>
                        <th>Invoice ID</th>
                        <th style={{ whiteSpace: "nowrap", width: 120 }}>Date-Time</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Added By</th>
                        <th>Transaction</th>
                        <th>Branch</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPayments.length === 0 ? (
                        <tr>
                          <td
                            colSpan="9"
                            className="text-center text-muted py-4"
                          >
                            No payments found
                          </td>
                        </tr>
                      ) : (
                        currentPayments.map((payment, index) => {
                          const paymentLocked = recordLockHours != null && (() => {
                            const ts = payment.payment_date
                            if (!ts) return false
                            return (Date.now() - new Date(ts).getTime()) > recordLockHours * 3600 * 1000
                          })()
                          return (<tr key={index}>
                            <td>{payment.sn || index + 1}</td>
                            <td>
                              {payment.invoice_no || payment.invoice_id || payment.invoiceId
                                ? (payment.invoice_no || payment.invoice_id || payment.invoiceId)
                                : <span className="badge bg-warning text-dark">Advance</span>}
                            </td>
                            <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                              {(() => {
                                const d =
                                  payment.payment_date ||
                                  payment.date ||
                                  payment.created_at;
                                if (!d) return "-";
                                const date = new Date(d);
                                return (
                                  <>
                                    <div>{date.toLocaleDateString("en-GB")}</div>
                                    <div className="text-muted">{date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}</div>
                                  </>
                                );
                              })()}
                            </td>
                            <td>
                              {Number(
                                payment.payment_amount ?? payment.amount ?? 0
                              ).toFixed(3)}{" "}
                              BHD
                            </td>
                            <td>
                              {payment.payment_method || payment.method || "-"}
                            </td>
                            <td>
                              {payment.sent_by || "-"}
                            </td>
                            <td>
                              {payment.transactionId ||
                                payment.transaction_id ||
                                payment.transaction ||
                                "-"}
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {(() => {
                                const branchId = payment.branch_id;
                                if (!branchId) return "-";
                                if (allBranchSettings && allBranchSettings.length > 0) {
                                  const idx = allBranchSettings.findIndex(b => b.id === branchId);
                                  if (idx !== -1) {
                                    const branch = allBranchSettings[idx];
                                    return branch?.official_name || `Branch ${idx + 1}`;
                                  }
                                }
                                // Fallback: check activeBranchSettings
                                const active = JSON.parse(localStorage.getItem('activeBranchSettings') || 'null');
                                if (active && active.id === branchId) return active.official_name || "Main Branch";
                                return `Branch #${branchId}`;
                              })()}
                            </td>
                            <td className="text-center">
                              <button
                                className="avtar avtar-xs btn-link-secondary viewIcon me-2"
                                title="View"
                                onClick={() => {
                                  setActivePayment(payment);
                                  setPayDetailOpen(true);
                                }}
                              >
                                <i className="ti ti-eye f-20"></i>
                                <span>View</span>
                              </button>
                              {!payment.invoice_id && !payment.invoiceId && (
                                <button
                                  className="avtar avtar-xs btn-link-secondary me-2"
                                  title="Link to Invoice"
                                  onClick={() => {
                                    setLinkPayment(payment);
                                    setLinkRows([{ invoice_id: "", amount: "" }]);
                                    setLinkOpen(true);
                                  }}
                                >
                                  <i className="ti ti-link f-20"></i>
                                </button>
                              )}



                              <button
                                className="avatar avatar-xs btn-link-secondary deleteIcon"
                                title={paymentLocked ? "Record is locked" : "Reverse Payment"}
                                disabled={paymentLocked}
                                style={{
                                  cursor: paymentLocked ? "not-allowed" : "pointer",
                                  opacity: paymentLocked ? 0.5 : 1,
                                }}
                                onClick={() => {
                                  if (!paymentLocked) handleDeletePayment(payment.payment_id || payment.id)
                                }}
                              >
                                <i className="ti ti-trash f-20"></i>
                                {/* <span>Reverse</span> */}

                              </button>


                              <button
                                className="btn btn-link p-0 text-decoration-none"
                                title="Pay"
                                onClick={() =>
                                  openPaymentForInvoice(
                                    invoices.find(
                                      (inv) =>
                                        (inv.invoice_no ||
                                          inv.invoiceNo ||
                                          inv.id) ===
                                        (payment.invoice_id ||
                                          payment.invoiceId)
                                    )
                                  )
                                }
                              >
                                {/* <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="feather feather-credit-card text-success"
                                >
                                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                  <line x1="1" y1="10" x2="23" y2="10"></line>
                                </svg> */}
                              </button>
                            </td>
                          </tr>
                        )})
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination for Payments */}
                {/* {totalPaymentPages > 1 && (
                  <div className="d-flex justify-content-end mt-3">
                    <Pagination
                      currentPage={currentPaymentPage}
                      totalPages={totalPaymentPages}
                      setCurrentPage={setCurrentPaymentPage}
                    />
                  </div>
                )} */}
                {totalPaymentPages > 1 && (
                  <nav className="tablepagination">
                    <ul className="pagination justify-content-end mb-0 me-3">
                      <li
                        className={`page-item ${currentPaymentPage === 1 ? "disabled" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            currentPaymentPage > 1 &&
                            setCurrentPaymentPage(currentPaymentPage - 1)
                          }
                        >
                          &laquo;
                        </button>
                      </li>
                      <li className="pageNmbr">{`${currentPaymentPage} of ${totalPaymentPages}`}</li>
                      <li
                        className={`page-item ${currentPaymentPage === totalPaymentPages
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            currentPaymentPage < totalPaymentPages &&
                            setCurrentPaymentPage(currentPaymentPage + 1)
                          }
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>

            {/* Installment Plans Panel */}
            <div className="cardBorder mt-3">
              <div className="tableHeader">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Installment Plans</h5>
                  <button
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ fontSize: 13, padding: "5px 12px" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#ACF39D"; e.currentTarget.style.borderColor = "#ACF39D"; e.currentTarget.style.color = "#1a1f36"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = ""; e.currentTarget.style.color = ""; }}
                    onClick={openCreateInstallment}
                  >
                    Create Installment
                  </button>
                </div>
              </div>
              <div>
                <div className="table-responsive w-100">
                  <table className="table w-100">
                    <thead>
                      <tr>
                        <th>Plan #</th>
                        <th>Invoices</th>
                        <th>Total Amount</th>
                        <th>Progress</th>
                        <th>Next Due</th>
                        <th>Status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {installmentPlansLoading ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">Loading...</td>
                        </tr>
                      ) : installmentPlans.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">No installment plans found</td>
                        </tr>
                      ) : (
                        installmentPlans.map((plan) => {
                          const totalPaid = Number(plan.total_paid || 0);
                          const totalAmount = Number(plan.total_amount || 0);
                          const statusColor =
                            plan.status === "completed" ? "bg-success" :
                              plan.status === "cancelled" ? "bg-secondary" : "bg-primary";
                          return (
                            <tr key={plan.id}>
                              <td>{plan.id}</td>
                              <td>{plan.invoice_numbers || "-"}</td>
                              <td>{totalAmount.toFixed(3)} BHD</td>
                              <td>
                                {plan.installments_paid || 0} of {plan.number_of_installments} paid
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                  {totalPaid.toFixed(3)} / {totalAmount.toFixed(3)} BHD
                                </div>
                              </td>
                              <td>
                                {plan.next_due_date
                                  ? (
                                    <>
                                      {new Date(plan.next_due_date).toLocaleDateString("en-GB")}
                                      <div className="text-muted" style={{ fontSize: 11 }}>
                                        {Number(plan.next_due_amount || 0).toFixed(3)} BHD
                                      </div>
                                    </>
                                  )
                                  : "-"}
                              </td>
                              <td>
                                <span className={`badge ${statusColor} text-capitalize`}>{plan.status}</span>
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-link p-0 text-decoration-none me-2"
                                  title="View"
                                  onClick={() => openViewPlan(plan)}
                                >
                                  <i className="ti ti-eye f-20"></i>
                                </button>
                                <button
                                  className="btn btn-link p-0 text-decoration-none me-2"
                                  title="Print Statement"
                                  onClick={() => printInstallmentStatement(plan)}
                                >
                                  <i className="ti ti-printer f-20"></i>
                                </button>
                                {plan.status === "active" && (
                                  <button
                                    className="btn btn-link p-0 text-decoration-none text-danger"
                                    title="Cancel Plan"
                                    onClick={() => handleCancelInstallmentPlan(plan)}
                                  >
                                    <i className="ti ti-x f-20"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create invoice modal */}
      {isOpen && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Invoice</h5>
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-center">
                  {/* <div>
                    <strong>Invoice No:</strong> {nextInvoiceNo}
                  </div> */}
                  {/* <div>
                    <strong>Total:</strong> ₹ {totalAmount}
                  </div> */}
                </div>

                <div className="row">
                  <div className="col-md-12 billingModal">
                    <label className="form-label">Select Services</label>
                    <div className="mb-2">
                      <Select
                        classNamePrefix="react-select"
                        options={serviceOptions}
                        value={selectedServiceOptions}
                        onChange={handleServiceSearchSelect}
                        placeholder="Search and select services..."
                        isSearchable={true}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                          menu: (base) => ({ ...base, zIndex: 2000 }),
                        }}
                        components={{
                          Option: ({ children, ...props }) => {
                            const { isSelected } = props;
                            return (
                              <div
                                {...props.innerProps}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  backgroundColor: props.isFocused
                                    ? "#deebff"
                                    : "white",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => { }}
                                  style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                  }}
                                />
                                <span>{children}</span>
                              </div>
                            );
                          },
                        }}
                        noOptionsMessage={() =>
                          services.length ? "No match" : "No services available"
                        }
                      />
                    </div>
                    <div className="list-group">
                      {/* {services.map((s) => (
                        <label
                          key={s.id}
                          className={`list-group-item d-flex align-items-center ${
                            selected.services[s.id] ? "" : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            // border: selected.services[s.id] ? '1px solid #0d6efd' : '1px solid transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            checked={!!selected.services[s.id]}
                            onChange={() => toggleService(s.id)}
                            style={{
                              accentColor: selected.services[s.id]
                                ? "#0d6efd"
                                : undefined,
                              transform: "scale(1.1)",
                              cursor: "pointer",
                            }}
                          />
                          <div className="flex-grow-1">
                            <div>
                              <strong
                                className={
                                  selected.services[s.id] ? "text-primary" : ""
                                }
                              >
                                {s.service_name}
                              </strong>{" "}
                              <span className="text-muted">
                                ({s.service_code || s.code})
                              </span>
                            </div>
                            <div className="text-muted small">
                              Net: {s.netAmount ?? s.net_amount} BHD
                            </div>
                          </div>
                        </label>
                      ))} */}
                    </div>

                    <label className="form-label">Select Lab Services</label>
                    <div className="mb-2">
                      <Select
                        classNamePrefix="react-select"
                        options={labOptions}
                        value={selectedLabOptions}
                        onChange={handleLabSearchSelect}
                        placeholder="Search and select lab services..."
                        isSearchable={true}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                          menu: (base) => ({ ...base, zIndex: 2000 }),
                        }}
                        components={{
                          Option: ({ children, ...props }) => {
                            const { isSelected } = props;
                            return (
                              <div
                                {...props.innerProps}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  backgroundColor: props.isFocused
                                    ? "#deebff"
                                    : "white",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => { }}
                                  style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                  }}
                                />
                                <span>{children}</span>
                              </div>
                            );
                          },
                        }}
                        noOptionsMessage={() =>
                          labRequests.length
                            ? "No match"
                            : "No lab services available"
                        }
                      />
                    </div>

                    <label className="form-label">Select Inventory</label>
                    <div className="mb-2">
                      <Select
                        classNamePrefix="react-select"
                        options={inventoryOptions}
                        value={selectedInventoryOptions}
                        onChange={handleInventorySearchSelect}
                        placeholder="Search and select inventory items..."
                        isSearchable={true}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                          menu: (base) => ({ ...base, zIndex: 2000 }),
                        }}
                        components={{
                          Option: ({ children, ...props }) => {
                            const { isSelected } = props;
                            return (
                              <div
                                {...props.innerProps}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  backgroundColor: props.isFocused
                                    ? "#deebff"
                                    : "white",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => { }}
                                  style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                  }}
                                />
                                <span>{children}</span>
                              </div>
                            );
                          },
                        }}
                        noOptionsMessage={() =>
                          inventory.length
                            ? "No match"
                            : "No inventory available"
                        }
                      />
                    </div>
                    <div className="list-group">
                      {/* {inventory.map((i) => (
                        <label
                          key={i.id}
                          className={`list-group-item d-flex align-items-center ${
                            selected.inventory[i.id] ? "" : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            // border: selected.inventory[i.id] ? '1px solid #0d6efd' : '1px solid transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            checked={!!selected.inventory[i.id]}
                            onChange={() => toggleInventory(i.id)}
                            style={{
                              accentColor: selected.inventory[i.id]
                                ? "#0d6efd"
                                : undefined,
                              transform: "scale(1.1)",
                              cursor: "pointer",
                            }}
                          />
                          <div className="flex-grow-1">
                            <div>
                              <strong
                                className={
                                  selected.inventory[i.id] ? "text-primary" : ""
                                }
                              >
                                {i.name}
                              </strong>{" "}
                              <span className="text-muted">({i.code})</span>
                            </div>
                            <div className="text-muted small">
                              Price: {i.price} BHD
                            </div>
                          </div>
                        </label>
                      ))} */}
                    </div>
                  </div>
                </div>
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div className="px-3 pb-2">
                  <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Branch</label>
                  <select
                    className="form-select form-select-sm"
                    value={invoiceBranchId || ""}
                    onChange={(e) => setInvoiceBranchId(parseInt(e.target.value) || null)}
                  >
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={b.id}>
                        Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button className="cancelText" onClick={() => setIsOpen(false)}>
                  Close
                </button>
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ fontSize: 16, padding: "5px 12px", backgroundColor: "#fff", color: "var(--green)" }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#ACF39D"; e.currentTarget.style.color = "var(--green)"; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#fff"; e.currentTarget.style.color = "var(--green)"; }}
                  disabled={loading}
                  onClick={createInvoice}
                >
                  {loading ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpen && <div className="modal-backdrop fade show"></div>}

      {detailOpen && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invoice Details</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setDetailOpen(false);
                    setActiveInvoice(null);
                    setInvoiceDetails(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {!invoiceDetails && (
                  <div className="text-center text-muted">Loading...</div>
                )}
                {invoiceDetails && (
                  <div>
                    <div className="d-flex justify-content-between mb-3">
                      <div>
                        <strong>Invoice No:</strong>{" "}
                        {invoiceDetails.invoiceNo || ""}
                      </div>
                      <div>
                        <strong>Date:</strong>{" "}
                        {(() => {
                          try {
                            const date = new Date(invoiceDetails.date);
                            return isNaN(date.getTime())
                              ? ""
                              : date.toLocaleDateString("en-GB");
                          } catch (e) {
                            return "";
                          }
                        })()}
                      </div>
                    </div>
                    {/* Invoice Summary */}
                    <div className="row mb-6">
                      <div className="col-md-12">
                        <div className="card bg-light">
                          <div className="card-body">
                            <h6 className="card-title">Invoice Summary</h6>
                            <p className="mb-1">
                              <strong>Total Services:</strong>{" "}
                              {invoiceDetails.totalServices || ""}
                            </p>
                            <p className="mb-1">
                              <strong>Total Amount:</strong>{" "}
                              {Number(invoiceDetails.total || 0).toFixed(3)} BHD
                            </p>
                            <p className="mb-1">
                              <strong>Paid Amount:</strong>{" "}
                              {Number(invoiceDetails.paidAmount || 0).toFixed(
                                3
                              )}{" "}
                              BHD
                            </p>
                            <p className="mb-0">
                              <strong>Remaining Amount:</strong>{" "}
                              {Number(
                                invoiceDetails.remainingAmount || 0
                              ).toFixed(3)}{" "}
                              BHD
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* <div className="col-md-6">
                        <div className="card bg-light">
                          <div className="card-body">
                            <h6 className="card-title">Status</h6>
                            <p className="mb-0">
                              <span className={`badge ${(() => {
                                const status = (invoiceDetails.status || "").toString().toLowerCase();
                                if (status.includes('paid') || status === 'fully paid') {
                                  return "bg-success";
                                } else if (status.includes('remaining') || status.includes('partially')) {
                                  return "bg-warning";
                                } else {
                                  return "bg-danger";
                                }
                              })()} px-3 py-2`}>
                                {invoiceDetails.status || 'Unknown'}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div> */}
                    </div>

                    {/* Services and Inventory Details */}
                    {(invoiceDetails.services &&
                      invoiceDetails.services.length > 0) ||
                      (invoiceDetails.inventory &&
                        invoiceDetails.inventory.length > 0) ? (
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Code</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Price</th>
                              <th className="text-end">Discount</th>
                              <th className="text-end">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(invoiceDetails.services || []).map((s, idx) => (
                              <tr key={`s-${idx}`}>
                                <td>{idx + 1}</td>
                                <td title={s.name || ""}>
                                  {truncateText(s.name, 30)}
                                </td>
                                <td>{s.code || ""}</td>
                                <td className="text-end">{s.quantity ?? 1}</td>
                                <td className="text-end">
                                  {Number(s.price || 0).toFixed(3)} BHD
                                </td>
                                <td className="text-end">
                                  {Number(s.discount || 0).toFixed(3)} BHD
                                </td>
                                <td className="text-end">
                                  {Number(s.netAmount || 0).toFixed(3)} BHD
                                </td>
                              </tr>
                            ))}
                            {(invoiceDetails.inventory || []).map((i, idx) => (
                              <tr key={`i-${idx}`}>
                                <td>
                                  {(invoiceDetails.services?.length || 0) +
                                    idx +
                                    1}
                                </td>
                                <td title={i.name || ""}>
                                  {truncateText(i.name, 30)}
                                </td>
                                <td>{i.code || ""}</td>
                                <td className="text-end">{Number(i.quantity || 1)}</td>
                                <td className="text-end">
                                  {Number(i.price || 0).toFixed(3)} BHD
                                </td>
                                <td className="text-end">
                                  {Number(i.discount || 0).toFixed(3)} BHD
                                </td>
                                <td className="text-end">
                                  {Number(i.totalPrice || 0).toFixed(3)} BHD
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan="6" className="text-end">
                                <strong>Total</strong>
                              </td>
                              <td className="text-end">
                                <strong>
                                  {(invoiceDetails.total ?? 0).toFixed(3)} BHD
                                </strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div>
                        {/* <i className="ti ti-info-circle me-2"></i> */}
                        {/* Detailed item breakdown is not available for this invoice.  */}
                        {/* Please refer to the summary information above. */}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="cancelText"
                  onClick={() => setDetailOpen(false)}
                >
                  Close
                </button>
                {/* <button className="btn btn-outline-primary" onClick={printInvoice} disabled={!invoiceDetails}>Print</button> */}
                {/* <button className="btn btn-primary" onClick={emailInvoice} disabled={!invoiceDetails || emailSending}>{emailSending ? "Sending..." : "Email"}</button> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {detailOpen && <div className="modal-backdrop fade show"></div>}

      {/* Date Range Dialog for Print Summary */}
      {showDateRangeDialog && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Select Date Range for Summary</h5>
                  {/* <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDateRangeDialog(false)}
                  ></button> */}
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateRange.startDate}
                      onChange={(e) =>
                        setDateRange({
                          ...dateRange,
                          startDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateRange.endDate}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, endDate: e.target.value })
                      }
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancelText"
                    onClick={() => setShowDateRangeDialog(false)}
                    disabled={emailSending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ fontSize: 13, padding: "5px 12px" }}
                    onClick={handlePrintSummary}
                    disabled={emailSending}
                  >
                    Print Summary
                  </button>
                  <button
                    type="button"
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ fontSize: 13, padding: "5px 12px" }}
                    onClick={sentEmailSummary}
                    disabled={emailSending}
                  >
                    {emailSending ? "Sending..." : "Email"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Payment modal */}
      {payOpen && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Add Payment - {payMeta.cashier || "User"}
                </h5>
              </div>
              <div className="modal-body ">
                <div className=" d-flex justify-content-between flex-wrap">
                  <div className="me-3">
                    <strong>Cashier:</strong> {payMeta.cashier || "-"}
                  </div>
                  <div>
                    <strong>Time:</strong> {payMeta.time || "-"}
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 mt-2">
                  <button
                    type="button"
                    className={`btn btn-sm flex-fill ${!payForm.is_advance ? "bgBtn" : "btn-outline-secondary"}`}
                    onClick={() => setPayForm({ ...payForm, is_advance: false, invoice_id: "" })}
                  >
                    Invoice Payment
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm flex-fill ${payForm.is_advance ? "bgBtn" : "btn-outline-secondary"}`}
                    onClick={() => setPayForm({ ...payForm, is_advance: true, invoice_id: "" })}
                  >
                    Advance Payment
                  </button>
                </div>
                {!payForm.is_advance && (
                <div>
                  <label className="form-label">Invoice ID</label>
                  <select
                    className="form-select"
                    value={payForm.invoice_id}
                    onChange={handleInvoiceSelect}
                  >
                    <option value="">Select invoice</option>
                    {remainingInvoices.map((inv) => {
                      const id = String(
                        inv.invoiceId ||
                        inv.invoice_id ||
                        inv.id ||
                        inv.invoiceid
                      );
                      const labelNo = inv.invoice_no || inv.invoiceNo || id;
                      const remaining =
                        inv.remaining_amount ??
                        inv.remaining ??
                        inv.remainingAmount ??
                        0;
                      const dateStr =
                        inv.invoice_date ||
                        inv.updated_at ||
                        inv.updatedAt ||
                        inv.date;
                      const dateLabel = dateStr
                        ? new Date(dateStr).toLocaleDateString("en-GB")
                        : "-";
                      return (
                        <option key={id} value={id}>
                          {labelNo} - Rem: {Number(remaining).toFixed(3)} -{" "}
                          {dateLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
                )}
                <div>
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={payForm.amount}
                    onChange={(e) =>
                      setPayForm({ ...payForm, amount: e.target.value })
                    }
                    min="0"
                    step="0.001"
                  />
                </div>
                <div>
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select"
                    value={payForm.payment_method}
                    onChange={(e) =>
                      setPayForm({ ...payForm, payment_method: e.target.value })
                    }
                  >
                    <option value="">Select payment method</option>
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.name}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">
                    Transaction ID (optional)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={payForm.transaction_id}
                    onChange={(e) =>
                      setPayForm({ ...payForm, transaction_id: e.target.value })
                    }
                    placeholder="Enter transaction/reference ID"
                  />
                </div>
                <div>
                  <label className="form-label">Branch</label>
                  <select
                    className="form-select"
                    value={payBranchId || ""}
                    onChange={(e) => setPayBranchId(parseInt(e.target.value) || null)}
                  >
                    <option value="">Select branch</option>
                    {(allBranchSettings || []).map((b, i) => (
                      <option key={b.id} value={b.id}>
                        Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="cancelText"
                  onClick={() => {
                    setPayOpen(false);
                    setPayForm({ invoice_id: "", amount: "", payment_method: "", transaction_id: "", is_advance: false });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ fontSize: 13, padding: "5px 12px" }}
                  disabled={payLoading}
                  onClick={payForm.is_advance ? submitAdvancePayment : submitPayment}
                >
                  {payLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {payOpen && <div className="modal-backdrop fade show"></div>}

      {/* Link Advance Payment to Invoice modal */}
      {linkOpen && (() => {
        const paymentAmount = Number(linkPayment?.payment_amount ?? linkPayment?.amount ?? 0);
        const totalAllocated = linkRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const remaining = paymentAmount - totalAllocated;

        const updateRow = (i, field, value) => {
          setLinkRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
        };
        const addRow = () => setLinkRows(prev => [...prev, { invoice_id: "", amount: "" }]);
        const removeRow = (i) => setLinkRows(prev => prev.filter((_, idx) => idx !== i));

        return (
          <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Link Advance Payment to Invoice(s)</h5>
                  </div>
                  <div className="modal-body">

                    {/* Summary bar */}
                    <div className="d-flex gap-3 mb-3 p-2 rounded" style={{ background: "#f8f9fb", border: "1px solid #e8ecf0", fontSize: 13 }}>
                      <span><strong>Payment:</strong> {paymentAmount.toFixed(3)} BHD</span>
                      <span><strong>Allocated:</strong> <span style={{ color: totalAllocated > paymentAmount ? "#dc2626" : "#2CA87F" }}>{totalAllocated.toFixed(3)} BHD</span></span>
                      <span><strong>Remaining:</strong> <span style={{ color: remaining < -0.001 ? "#dc2626" : "#5B6B79" }}>{remaining.toFixed(3)} BHD</span></span>
                    </div>

                    {/* Invoice rows */}
                    {linkRows.map((row, i) => {
                      const selectedInv = remainingInvoices.find(inv =>
                        String(inv.invoiceId || inv.invoice_id || inv.id || inv.invoiceid) === String(row.invoice_id)
                      );
                      const invRemaining = selectedInv
                        ? Number(selectedInv.remaining_amount ?? selectedInv.remaining ?? selectedInv.remainingAmount ?? 0)
                        : null;

                      return (
                        <div key={i} className="d-flex align-items-end gap-2 mb-2">
                          <div style={{ flex: 2 }}>
                            {i === 0 && <label className="form-label mb-1" style={{ fontSize: 12 }}>Invoice</label>}
                            <select
                              className="form-select form-select-sm"
                              value={row.invoice_id}
                              onChange={e => {
                                const invId = e.target.value;
                                const inv = remainingInvoices.find(x =>
                                  String(x.invoiceId || x.invoice_id || x.id || x.invoiceid) === invId
                                );
                                const cap = inv ? Number(inv.remaining_amount ?? inv.remaining ?? inv.remainingAmount ?? 0) : 0;
                                const autoAmount = Math.min(cap, remaining + (Number(row.amount) || 0));
                                updateRow(i, "invoice_id", invId);
                                updateRow(i, "amount", autoAmount > 0 ? autoAmount.toFixed(3) : "");
                              }}
                            >
                              <option value="">Select invoice</option>
                              {remainingInvoices.map((inv) => {
                                const id = String(inv.invoiceId || inv.invoice_id || inv.id || inv.invoiceid);
                                const labelNo = inv.invoice_no || inv.invoiceNo || id;
                                const rem = Number(inv.remaining_amount ?? inv.remaining ?? inv.remainingAmount ?? 0);
                                const dateStr = inv.invoice_date || inv.updated_at || inv.updatedAt || inv.date;
                                const dateLabel = dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "-";
                                const alreadySelected = linkRows.some((r, ri) => ri !== i && r.invoice_id === id);
                                return (
                                  <option key={id} value={id} disabled={alreadySelected}>
                                    {labelNo} — Rem: {rem.toFixed(3)} — {dateLabel}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                          <div style={{ flex: 1 }}>
                            {i === 0 && <label className="form-label mb-1" style={{ fontSize: 12 }}>Amount (BHD)</label>}
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              placeholder="0.000"
                              step="0.001"
                              min="0.001"
                              max={invRemaining ?? undefined}
                              value={row.amount}
                              onChange={e => updateRow(i, "amount", e.target.value)}
                            />
                            {invRemaining !== null && (
                              <div style={{ fontSize: 11, color: "#5B6B79", marginTop: 2 }}>
                                Max: {invRemaining.toFixed(3)}
                              </div>
                            )}
                          </div>
                          <div style={{ paddingBottom: invRemaining !== null ? 20 : 0 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ width: 30, height: 30, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                              disabled={linkRows.length === 1}
                              onClick={() => removeRow(i)}
                            >
                              <i className="ti ti-trash" style={{ fontSize: 14 }} />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-1"
                      onClick={addRow}
                      disabled={remainingInvoices.length <= linkRows.length || remaining <= 0.001}
                    >
                      <i className="ti ti-plus" style={{ fontSize: 13 }} /> Add Invoice
                    </button>

                    {totalAllocated > paymentAmount + 0.001 && (
                      <div className="alert alert-danger mt-3 py-2 mb-0" style={{ fontSize: 12 }}>
                        Total allocated exceeds payment amount by {(totalAllocated - paymentAmount).toFixed(3)} BHD.
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="cancelText"
                      onClick={() => { setLinkOpen(false); setLinkPayment(null); setLinkRows([{ invoice_id: "", amount: "" }]); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="bgBtn btn-sm d-flex align-items-center gap-1"
                      style={{ fontSize: 13, padding: "5px 12px" }}
                      disabled={linkLoading}
                      onClick={submitLinkAdvancePayment}
                    >
                      {linkLoading ? "Saving..." : "Link"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        );
      })()}

      {/* Payment Detail modal */}
      {payDetailOpen && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Payment Details</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setPayDetailOpen(false);
                    setActivePayment(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {!activePayment && (
                  <div className="text-center text-muted">Loading...</div>
                )}
                {activePayment && (
                  <div className="card-body bg-white rounded-bottom-4 p-2">
                    <p className="mb-2">
                      <span className="fw-semibold">Invoice:</span>{" "}
                      {activePayment.invoice_no ||
                        activePayment.invoice_id ||
                        activePayment.invoiceId ||
                        "-"}
                    </p>
                    <p className="mb-2">
                      <span className="fw-semibold">Amount:</span>{" "}
                      {Number(
                        activePayment.payment_amount ??
                        activePayment.amount ??
                        0
                      ).toFixed(3)}{" "}
                      BHD
                    </p>
                    <p className="mb-2">
                      <span className="fw-semibold">Method:</span>{" "}
                      {activePayment.payment_method ||
                        activePayment.method ||
                        "-"}
                    </p>
                    <p className="mb-2">
                      <span className="fw-semibold">Transaction:</span>{" "}
                      {activePayment.transactionId ||
                        activePayment.transaction_id ||
                        "-"}
                    </p>
                    <p className="mb-0">
                      <span className="fw-semibold">Date:</span>{" "}
                      {(() => {
                        const d =
                          activePayment.payment_date ||
                          activePayment.date ||
                          activePayment.created_at;
                        if (!d) return "-";
                        const dt = new Date(d);
                        return `${dt.toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })} ${dt.toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        })}`;
                      })()}
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="cancelText"
                  onClick={() => setPayDetailOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {payDetailOpen && <div className="modal-backdrop fade show"></div>}

      {/* Create Installment Plan modal */}
      {createInstallmentOpen && (() => {
        const preview = buildInstallmentPreview(createInstallmentForm, remainingInvoices);
        return (
          <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Create Installment Plan</h5>
                  </div>
                  <div className="modal-body">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Select Invoice(s)</label>
                    <div
                      className="mb-3"
                      style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e8ecf0", borderRadius: 6, padding: 8 }}
                    >
                      {remainingInvoices.length === 0 ? (
                        <div className="text-muted" style={{ fontSize: 13 }}>No unpaid invoices found</div>
                      ) : (
                        remainingInvoices.map((inv) => {
                          const id = String(inv.invoiceId || inv.invoice_id || inv.id || inv.invoiceid);
                          const labelNo = inv.invoice_no || inv.invoiceNo || id;
                          const rem = Number(inv.remaining_amount ?? inv.remaining ?? inv.remainingAmount ?? 0);
                          const dateStr = inv.invoice_date || inv.updated_at || inv.updatedAt || inv.date;
                          const dateLabel = dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "-";
                          const checked = createInstallmentForm.invoice_ids.includes(id);
                          return (
                            <div key={id} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`inst-inv-${id}`}
                                checked={checked}
                                onChange={() => toggleInstallmentInvoice(id)}
                              />
                              <label className="form-check-label" htmlFor={`inst-inv-${id}`} style={{ fontSize: 13 }}>
                                {labelNo} — Rem: {rem.toFixed(3)} BHD — {dateLabel}
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="d-flex gap-3 mb-3 p-2 rounded" style={{ background: "#f8f9fb", border: "1px solid #e8ecf0", fontSize: 13 }}>
                      <span><strong>Total Selected:</strong> {preview.totalAmount.toFixed(3)} BHD</span>
                    </div>

                    <div className="mb-3">
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Plan Type</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="plan-type-months"
                            checked={createInstallmentForm.plan_type === "months"}
                            onChange={() => setCreateInstallmentForm((prev) => ({ ...prev, plan_type: "months" }))}
                          />
                          <label className="form-check-label" htmlFor="plan-type-months" style={{ fontSize: 13 }}>
                            By number of months
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="plan-type-amount"
                            checked={createInstallmentForm.plan_type === "amount"}
                            onChange={() => setCreateInstallmentForm((prev) => ({ ...prev, plan_type: "amount" }))}
                          />
                          <label className="form-check-label" htmlFor="plan-type-amount" style={{ fontSize: 13 }}>
                            By installment amount
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      {createInstallmentForm.plan_type === "months" ? (
                        <div className="col-md-6 mb-3">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Number of Months</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="1"
                            step="1"
                            value={createInstallmentForm.months}
                            onChange={(e) => setCreateInstallmentForm((prev) => ({ ...prev, months: e.target.value }))}
                          />
                          {preview.regularAmount != null && (
                            <div style={{ fontSize: 11, color: "#5B6B79", marginTop: 2 }}>
                              = {preview.regularAmount.toFixed(3)} BHD / installment
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="col-md-6 mb-3">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>Installment Amount (BHD)</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="0.001"
                            step="0.001"
                            value={createInstallmentForm.installment_amount}
                            onChange={(e) => setCreateInstallmentForm((prev) => ({ ...prev, installment_amount: e.target.value }))}
                          />
                          {preview.numberOfInstallments != null && (
                            <div style={{ fontSize: 11, color: "#5B6B79", marginTop: 2 }}>
                              = {preview.numberOfInstallments} installments
                            </div>
                          )}
                        </div>
                      )}
                      <div className="col-md-3 mb-3">
                        <label className="form-label mb-1" style={{ fontSize: 12 }}>Start Date</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={createInstallmentForm.start_date}
                          onChange={(e) => setCreateInstallmentForm((prev) => ({ ...prev, start_date: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label mb-1" style={{ fontSize: 12 }}>Frequency</label>
                        <select
                          className="form-select form-select-sm"
                          value={createInstallmentForm.frequency}
                          onChange={(e) => setCreateInstallmentForm((prev) => ({ ...prev, frequency: e.target.value }))}
                        >
                          <option value="monthly">Monthly</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Notes (optional)</label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        value={createInstallmentForm.notes}
                        onChange={(e) => setCreateInstallmentForm((prev) => ({ ...prev, notes: e.target.value }))}
                      />
                    </div>

                    {preview.schedule.length > 0 && (
                      <div>
                        <label className="form-label mb-1" style={{ fontSize: 12 }}>Schedule Preview</label>
                        <div className="table-responsive" style={{ maxHeight: 200, overflowY: "auto" }}>
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Due Date</th>
                                <th>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.schedule.map((row) => (
                                <tr key={row.installment_no}>
                                  <td>{row.installment_no}</td>
                                  <td>{row.due_date.toLocaleDateString("en-GB")}</td>
                                  <td>{row.amount.toFixed(3)} BHD</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="cancelText"
                      onClick={() => setCreateInstallmentOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="bgBtn btn-sm d-flex align-items-center gap-1"
                      style={{ fontSize: 13, padding: "5px 12px" }}
                      disabled={createInstallmentLoading}
                      onClick={submitCreateInstallment}
                    >
                      {createInstallmentLoading ? "Saving..." : "Create Plan"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        );
      })()}

      {/* View Installment Plan modal */}
      {viewPlanOpen && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Installment Plan #{activePlan?.id}</h5>
                </div>
                <div className="modal-body">
                  {activePlanLoading ? (
                    <div className="text-center text-muted py-4">Loading...</div>
                  ) : !activePlan ? (
                    <div className="text-center text-muted py-4">Plan not found</div>
                  ) : (
                    <>
                      <div className="d-flex gap-3 mb-3 p-2 rounded" style={{ background: "#f8f9fb", border: "1px solid #e8ecf0", fontSize: 13 }}>
                        <span><strong>Total:</strong> {Number(activePlan.total_amount).toFixed(3)} BHD</span>
                        <span><strong>Frequency:</strong> <span className="text-capitalize">{activePlan.frequency}</span></span>
                        <span><strong>Status:</strong> <span className="text-capitalize">{activePlan.status}</span></span>
                      </div>

                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Invoices Covered</label>
                      <div className="mb-3" style={{ fontSize: 13 }}>
                        {(activePlan.invoices || []).map((inv) => (
                          <span key={inv.invoice_id} className="badge bg-light text-dark border me-2 mb-1">
                            {inv.invoice_no} — {Number(inv.allocated_amount).toFixed(3)} BHD
                          </span>
                        ))}
                      </div>

                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Schedule</label>
                      <div className="table-responsive w-100">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Due Date</th>
                              <th>Amount</th>
                              <th>Paid</th>
                              <th>Status</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(activePlan.schedule || []).map((row) => {
                              const statusColor =
                                row.status === "paid" ? "bg-success" :
                                  row.status === "partial" ? "bg-warning text-dark" : "bg-secondary";
                              return (
                                <tr key={row.id}>
                                  <td>{row.installment_no}</td>
                                  <td>{new Date(row.due_date).toLocaleDateString("en-GB")}</td>
                                  <td>{Number(row.amount).toFixed(3)} BHD</td>
                                  <td>{Number(row.paid_amount).toFixed(3)} BHD</td>
                                  <td><span className={`badge ${statusColor} text-capitalize`}>{row.status}</span></td>
                                  <td className="text-center">
                                    {row.status !== "paid" && activePlan.status === "active" && (
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => openPayInstallment(row)}
                                      >
                                        Pay
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {(activePlan.payments || []).length > 0 && (
                        <>
                          <label className="form-label mb-1 mt-2" style={{ fontSize: 12 }}>Payment History</label>
                          <div className="table-responsive w-100">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Invoice</th>
                                  <th>Amount</th>
                                  <th>Method</th>
                                  <th>Added By</th>
                                </tr>
                              </thead>
                              <tbody>
                                {activePlan.payments.map((p) => (
                                  <tr key={p.id}>
                                    <td>{new Date(p.payment_date).toLocaleDateString("en-GB")}</td>
                                    <td>{p.invoice_id}</td>
                                    <td>{Number(p.payment_amount).toFixed(3)} BHD</td>
                                    <td>{p.payment_method}</td>
                                    <td>{p.sent_by_name || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="cancelText"
                    onClick={() => { setViewPlanOpen(false); setActivePlan(null); }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Pay Installment modal */}
      {payInstallmentOpen && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Pay Installment #{activeSchedule?.installment_no}</h5>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Amount (BHD)</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min="0.001"
                      step="0.001"
                      value={payInstallmentForm.amount}
                      onChange={(e) => setPayInstallmentForm((prev) => ({ ...prev, amount: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Payment Method</label>
                    <select
                      className="form-select form-select-sm"
                      value={payInstallmentForm.payment_method}
                      onChange={(e) => setPayInstallmentForm((prev) => ({ ...prev, payment_method: e.target.value }))}
                    >
                      {paymentMethods.map((m) => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Transaction ID (optional)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={payInstallmentForm.transaction_id}
                      onChange={(e) => setPayInstallmentForm((prev) => ({ ...prev, transaction_id: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="cancelText"
                    onClick={() => setPayInstallmentOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ fontSize: 13, padding: "5px 12px" }}
                    disabled={payInstallmentLoading}
                    onClick={submitPayInstallment}
                  >
                    {payInstallmentLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}

// Pagination component
function Pagination({ currentPage, totalPages, setCurrentPage }) {
  return (
    <div className="d-flex align-items-center gap-3">
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      >
        &lt;&lt;
      </button>
      <span>
        {currentPage}/{totalPages}
      </span>
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        &gt;&gt;
      </button>
    </div>
  );
}
